/**
 * The one place that talks to the backend.
 *
 * 🔴 A REFUSAL ARRIVES INTACT. The Windmill app runtime rejected with
 * `new Error(result.stack ?? result.message)`, so a refusal's sentence was discarded before
 * our code saw it — 68 lines of `humanError` existed to dig it back out. An HTTP body carries
 * whatever we put in it, so that whole layer is gone. What survives is the PREFIX CONVENTION:
 * `Refused:` names a decision and a remedy, `Infrastructure:` names something broken, and the
 * UI renders the two differently because they need different reactions from a person.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly kind: "refused" | "infrastructure" | "auth" | "unknown",
    readonly consentUrl?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function isRefusal(message: string): boolean {
  return /^refused\b/i.test(message.trim());
}

function classify(status: number, detail: string): ApiError["kind"] {
  if (status === 401) return "auth";
  if (isRefusal(detail)) return "refused";
  if (/^infrastructure:/i.test(detail.trim())) return "infrastructure";
  return status >= 500 ? "infrastructure" : "unknown";
}

function csrf(): string {
  return document.cookie.match(/(?:^|;\s*)tc_csrf=([^;]+)/)?.[1] ?? "";
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(method === "GET" || method === "HEAD" ? {} : { "X-CSRF-Token": csrf() }),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 204) return undefined as T;

  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const detail =
      body && typeof body === "object" && "detail" in body && typeof body.detail === "string"
        ? body.detail
        : `${res.status} ${res.statusText}`;
    const consent =
      body && typeof body === "object" && "consent_url" in body &&
      typeof body.consent_url === "string" ? body.consent_url : undefined;
    throw new ApiError(res.status, detail, classify(res.status, detail), consent);
  }
  return body as T;
}

export interface CourseAccess {
  id: string;
  name: string;
  can_run_grading: boolean;
  can_edit_rubrics: boolean;
  can_edit_settings: boolean;
  can_manage_teams: boolean;
  can_release: boolean;
  write_allowed: boolean;
}

export interface Me {
  email: string;
  display_name: string;
  courses: CourseAccess[];
  /** 🔴 THE ONE FACT ABOUT A PERSON RATHER THAN A (PERSON, COHORT) PAIR. Administering is
   *  not a capability: somebody who may add a teacher may add them to any cohort, so a
   *  per-cohort row could only say "may administer, but only over there".
   *  ⚠️ IT HIDES A LINK AND GRANTS NOTHING. The server re-reads its own list on every admin
   *  request, so a client that sent `true` would still be refused by name. */
  is_owner: boolean;
  /** Whether this DEPLOYMENT allows cohorts this console invented. It hides a nav link and
   *  grants nothing: every demo route re-reads the setting. */
  demo_enabled: boolean;
}

export const getMe = () => api<Me>("/me");

// ── cohorts this console invented ───────────────────────────────────────────────
//
// 🔴 A DEMO COHORT IS NOT IN CLASSROOM. Its students, their answers and its assignments were
// all made here, it can never write a grade anywhere, and it exists only where the deployment
// says so — which is why `enabled` travels even when it is false.

export interface DemoCohort {
  id: string;
  name: string;
  students: number;
  answers: number;
  assignments: number;
  proposals: number;
}

export interface DemoList {
  enabled: boolean;
  cohorts: DemoCohort[];
  /** The cohorts whose briefs can be copied in — the ones this teacher may already view. */
  sources: { id: string; name: string }[];
  you: string;
}

export const getDemoCohorts = () => api<DemoList>("/demo");
export const createDemoCohort = (name: string) =>
  api<DemoCohort>("/demo", { method: "POST", body: JSON.stringify({ name }) });
export const generateDemoStudents = (courseId: string, count: number) =>
  api<DemoCohort & { students: number; replaced: number }>(
    `/demo/${enc(courseId)}/students`,
    { method: "POST", body: JSON.stringify({ count }) });
export const copyDemoAssignments = (courseId: string, sourceCourseId: string) =>
  api<DemoCohort & { copied: number; already_here: number }>(
    `/demo/${enc(courseId)}/assignments`,
    { method: "POST", body: JSON.stringify({ source_course_id: sourceCourseId }) });
export const deleteDemoCohort = (courseId: string) =>
  api<{ deleted: string; people: number; intents: number }>(
    `/demo/${enc(courseId)}`, { method: "DELETE" });

// ── administering the console ───────────────────────────────────────────────────

export interface AdminTeacher {
  id: string;
  email: string;
  display_name: string;
  active: boolean;
  created_at: string;
  last_seen_at: string;
  /** cohort id → every capability. A cohort absent here is one they are not on. */
  courses: Record<string, Record<string, boolean>>;
}

export interface AdminTeachers {
  teachers: AdminTeacher[];
  /** ⚠️ THE COHORTS TRAVEL WITH IT. A grid built from the teachers alone can only show
   *  cohorts somebody is already on, so the one thing the page exists for would have no
   *  column to click. */
  courses: { id: string; name: string; write_allowed: boolean }[];
  capabilities: string[];
  you: string;
}

export const getAdminTeachers = () => api<AdminTeachers>("/admin/teachers");

export const addTeacher = (email: string, displayName = "") =>
  api<{ added: string; id: string; teachers: AdminTeacher[] }>("/admin/teachers", {
    method: "POST",
    body: JSON.stringify({ email, display_name: displayName }),
  });

export const setTeacherCapabilities = (
  teacherId: string, courseId: string, caps: Record<string, boolean>,
) =>
  api<{ changed: string[]; teachers: AdminTeacher[] }>(
    `/admin/teachers/${encodeURIComponent(teacherId)}/courses/${encodeURIComponent(courseId)}`,
    { method: "PUT", body: JSON.stringify(caps) },
  );

export const setTeacherActive = (teacherId: string, active: boolean) =>
  api<{ active: boolean; teachers: AdminTeacher[] }>(
    `/admin/teachers/${encodeURIComponent(teacherId)}/active`,
    { method: "PUT", body: JSON.stringify({ active }) },
  );
export const logout = () => api<{ signed_out: boolean }>("/auth/logout", { method: "POST" });
export const SIGN_IN_URL = "/api/auth/google/start";

// ── the eight tabs ──────────────────────────────────────────────────────────────

export interface CohortRow {
  submission_id: string; course_work_id: string; student: string; assignment: string;
  graded_at: string | null;
  /** 🔴 THE MARK A RELEASE WOULD POST — the reviewed one where a review is in force. */
  points: number | null; max_points: number | null;
  /** What the agent gave, whatever a review later decided. */
  agent_points: number | null;
  /** Who reviewed it, or null when no review is in force. */
  reviewed_by: string | null;
  status: "HELD" | "RELEASED" | "REVIEW" | "READY";
  // 🔴 `assignment` IS THE RUBRIC ID. It has held `vault-v1` since the column existed, and
  // the table's header said "Assignment" — so the one column naming the piece of work showed
  // the name of the scale it was marked against. This is the title Google gave it.
  assignment_title: string;
  /** Which language the mark was made in — the profile id and its label — or "" for a grade
   *  made before languages were detected (2026-09-27). `assignment` reads `generic-v1` on every
   *  grade since the language families were retired, so this is the column that tells them
   *  apart. */
  language: string; language_label: string;
  escalate: boolean; gradeable: boolean; released: boolean; outcome: string;
  problems: number; engine: string; model: string;
  /** How long this grading took and the tokens it used — usage, not a price (2026-10-03). */
  duration_ms: number | null; tokens_total: number | null; attribution: string | null;
  fingerprint_complete: boolean; alternate_link: string;
}
/** Input, output, read from cache and written to cache, summed across the models that ran. */
export interface Tokens { input: number; output: number; cache_read: number; cache_write: number }

/** One usage window of the subscription: a percentage, and when it resets. */
export interface UsageWindow { pct: number; resets_at: string | null }

/** One reading the Claude CLI made during a run: the 5-hour and weekly windows. */
export interface UsageReading { five_hour?: UsageWindow; seven_day?: UsageWindow; status?: string }

/** A run's first and last reading; `{}` when it read none (before CLI 2.1.288, or a gateway run). */
export interface UsageWindows { first?: UsageReading; last?: UsageReading; events?: number }

/** What one run used. Null where nothing was measured — never a zero standing in for unknown. */
export interface RunUsage {
  duration_ms: number | null; tokens: Tokens | null; tokens_total: number | null;
  /** The weekly figure at the run's first and last reading, or null when it read none. */
  weekly: { first: number | null; last: number | null; events: number } | null;
  /** Whether the run went through the gateway — paid per token, drawing nothing from the
   *  subscription. The server decides it from the stored route (`litellm`, `windmill-ai`). */
  gateway: boolean;
}

/** The subscription's newest reading and the two limits it is compared against. */
export interface UsageGrading {
  weekly: (UsageWindow & { expired: boolean }) | null;
  five_hour: (UsageWindow & { expired: boolean }) | null;
  read: boolean; read_at: string | null; read_by: string | null;
  pause_pct: number; paused: boolean;
  runs_today: number; runs_per_day: number; running_now: number; max_in_flight: number;
  state: string;
  /** When the subscription's own limit stops holding every run, a teacher's included, or null
   *  when none is in force. */
  run_held_until?: string | null;
  /** 🔴 WHETHER A TEACHER'S RUN GOES, AND THE SENTENCE SAYING WHY NOT — one server function
   *  (`subscription_usage.teacher_run_state`), so a disabled Run and its reason cannot disagree. */
  teacher_run_goes?: boolean;
  teacher_run?: string;
}

export interface Cohort {
  course: { id: string; name: string; write_allowed: boolean };
  rows: CohortRow[];
  counts: { total: number; review: number; ready: number; released: number; held: number };
}

/** One criterion this assignment adds to the baseline. Judged, never measured. */
export interface ExtraCriterion {
  id?: string;
  label: string;
  weight: number;
  credit_for?: string[];
}

export interface Binding {
  rubric_id: string | null; engine_id: string | null; auto_grade: boolean | null;
  guidance: string; rubric_set_by: string | null; engine_set_by: string | null;
  extra_criteria?: ExtraCriterion[];
  /** 🔴 WHETHER A SUBMISSION WITH NO TESTS IS MISSING SOMETHING — the assignment's call since
   *  2026-09-27, when the language families it used to live in were retired. Null inherits the
   *  base rubric's default. (The toolchain image is not here any more: it comes from the
   *  language detected per submission.) */
  testing_required: boolean | null; testing_required_set_by?: string | null;
  /** Does this assignment say what IT requires — extras, its own credit lines or notes,
   *  guidance? The same predicate as the Overview's `bound`. False means the base alone. */
  specific?: boolean;
  /** What this assignment does with each criterion it was given — `score`, `advisory` (judged
   *  and reported, worth nothing) or `off` (never sent to the model) — plus the teacher's own
   *  credit lines and a note. */
  criteria_overrides?: Record<string, CriterionOverride>;
  criteria_overrides_set_by?: string | null;
  /** 🔴 THE TASKS THE ASSIGNMENT ASKS FOR (2026-10-07), each with its requirements and a weight.
   *  A weight is a SHARE: the required tasks' weights are scaled to C1's points on every run.
   *  Absent from a server older than this. */
  tasks?: TaskDraft[];
  /** What the assignment is, above its tasks: context the grader reads, never marked. */
  tasks_summary?: string;
  tasks_set_by?: string | null;
}
/** A task as the Brief tab edits it and the binding stores it. The server mints `id`.
 *  🔴 THREE LEVELS (2026-10-07): a task holds its requirements and weight itself, or — where the
 *  brief numbers them — in subtasks, and is then worth their total. Optional and extra credit
 *  are the task's. A task stored before subtasks existed has no `description` or `subtasks`. */
export interface TaskDraft {
  id?: string;
  title: string;
  description?: string;
  /** Empty when the task has subtasks. */
  requirements: string[];
  optional: boolean;
  /** Only on an optional task: its points can make up what the required tasks lost. */
  extra_credit: boolean;
  /** Absent when the task has subtasks: they carry the weights. */
  weight?: number;
  subtasks?: SubtaskDraft[];
}
export interface SubtaskDraft {
  id?: string;
  title: string;
  description?: string;
  requirements: string[];
  weight: number;
}
export interface CriterionOverride {
  mode: "score" | "advisory" | "off";
  credit_for: string[];
  /** When true the rubric's own credit lines do not apply to this assignment and only the
   *  teacher's do. Default false, which appends — so a criterion cannot be silently redefined
   *  while keeping its id, label and weight. */
  replaces: boolean;
  note: string;
}
export interface Assignment {
  course_work_id: string; title: string; description: string;
  // DRAFT until somebody publishes. Every assignment this console creates starts here.
  state: string;
  max_points: number | null; work_type: string; alternate_link: string;
  due: unknown; associated_with_developer: boolean; binding: Binding | null; graded: number;
  /** When students were given it — the earliest submission Classroom made for it — or null for
   *  a draft, or work the sweep has not listed yet. The list arrives in this order, newest
   *  first, with drafts above. */
  published_at?: string | null;
  /** The four status words over the grades we hold for this assignment. `graded` alone made
   *  an assignment with two grades needing a human read identically to one ready to release. */
  ours: { graded: number; ready: number; review: number; held: number; released: number };
}
export interface Criterion {
  /** 🔴 `label`, NOT `text`. The rubric files have always spelled it `label`; the route asked
   *  for `text`, so every baseline criterion arrived null and rendered as a weight with an
   *  empty description. `credit_for` is the prose saying what the criterion is worth marks
   *  for — accepted server-side since extras existed, and shown on no screen until now. */
  id: string; weight: number; evidence: string; label: string | null;
  credit_for: string[];
}
export interface RubricInfo {
  id: string; sha256: string; suiteless: boolean; has_measured_criteria: boolean;
  is_baseline: boolean; selectable: boolean; brief: string; criteria: Criterion[];
  version: number;
  /** A retired rubric — one of the language families, since 2026-09-27 — is listed so a grade
   *  made under it still names something, and is never offered. */
  retired: boolean; retired_reason: string;
  /** What an assignment that has not said inherits for "tests expected". */
  testing_required: boolean;
}
/** How craft reads in one language: lines added to a criterion, which files are the student's
 *  own, and what such a project is tested with. Merged into the base per submission. */
export interface ProfileBody {
  id: string; label: string; brief?: string;
  craft: Record<string, string[]>;
  authored: string[]; ignored: string[];
  testing: { what: string[]; tools: string[] };
  [k: string]: unknown;
}
export interface ProfileInfo { id: string; label: string; version: number; body: ProfileBody }
export interface SubmissionRow {
  submission_id: string;
  /** Roster name, or "" until the roster is synced. NEVER the raw Google userId. */
  student: string; state: string; runnable: boolean; second_opinion: boolean;
  links: string[];
  history: { turned_in_at: string | null; resubmitted: boolean; withdrawn: boolean;
             turn_ins: number;
             /** 🔴 THE MARK CLASSROOM HOLDS — an OBJECT, not a number, and never a mark this
              *  pipeline wrote. Declared in full because a partial declaration is what let
              *  `String(grade_last)` typecheck and render `[object Object]` in the table. */
             grade_last: { points_earned: number | null; max_points: number | null;
                           at: string | null } | null };
  /** `points` is the mark a release would post; `status` is the word every screen uses —
   *  `escalate` alone kept a reviewed grade under "needs review". */
  ours: { points: number | null; agent_points: number | null; max_points: number | null;
          escalate: boolean; released: boolean; status: string; outcome: string } | null;
  queue: { reason: string; detail: string; requested: boolean; attempts: number } | null;
}

export interface RunRow {
  /** 🔴 WHICH COHORT THIS RUN IS FROM. Present on every row, not only when the list spans
   *  more than one — a field that appears conditionally is one every reader has to guard. */
  course_id: string;
  id: string; submission_id: string; course_work_id: string; attempt_no: number;
  engine: string; route: string; model: string; rubric_id: string | null;
  requested_by: string; started_at: string | null; finished_at: string | null;
  outcome: string; error_kind: string; error_message: string; wm_job_id: string;
  last_polled_at: string | null; poll_failures: number;
  envelope_received_at: string | null; processed_at: string | null;
  stored: boolean;
  /** 🔴 THE ALARM, computed once on the server (`runs.stored_nothing`): the attempt finished and
   *  no grade stands for it. A usage limit is not one. Read it; never re-derive it here. */
  stored_nothing: boolean;
  /** This attempt stored a grade that a later run has since replaced. Not a loss. */
  superseded: boolean; points: number | null;
  /** What this run used, from its own envelope: a run that stored nothing or was superseded
   *  still used what it used. */
  usage: RunUsage;
  /** How many times finalising this run's stored envelope has failed. */
  finalise_failures: number;
  /** When the collector finalises a HELD run again; null on every other outcome. */
  retry_finalise_at: string | null;
  /** The whole hash this run graded, with the link as dispatched; "" before runs were pinned. */
  commit_sha: string;
  repo_url: string;
  repo_subpath: string;
  /** 🔴 Whether Reprocess applies — computed by the rule the route enforces, never re-derived. */
  reprocessable: boolean;
}

export interface BuildLog {
  handle: string; kind: string; unavailable_reason: string; resolved: boolean;
  total: number; on_topic: number; off_topic: number; unclassified: number;
  // 🔴 NO TOPIC FIELD, HERE OR IN THE DATABASE. A post's record is a verdict; the evidence
  // is the linked post itself. The tag column went with the allowlist that constrained it —
  // keeping one without the other would have let a model write whatever it liked.
  last_post_at: string;
}

export interface Identity {
  signed_in: number; on_roster: number; matched: number;
  by_email: number; by_account: number; with_email: number; disagree: number;
}

export interface StudentRow {
  student: string; named: boolean; turned_in: number; returned: number; submitted: number;
  by_state: Record<string, number>; classroom_marks: number;
  our_grades: number; our_released: number; our_review: number;
  // Our own uuid, not the Google userId — a row has to be addressable, and this is an
  // identifier this system minted. Empty until the roster has been synced.
  id: string;
  /** 🔴 THE ADDRESS CLASSROOM HOLDS, which is what a person is matched by (2026-10-13).
   *  Teachers only; empty until the Classroom bot carries `classroom.profile.emails`. */
  email: string;
  build_log: BuildLog | null;
  /** 🔴 THE PUBLISHED TEAM'S NUMBER AND NAME, AND NOTHING THE MODEL SAID. A roster is a list
   *  of people; `level`, `skills` and the grouping rationale belong to the proposal review,
   *  where somebody is deciding whether to publish. `null` until a plan is published. */
  team: { number: number; name: string } | null;
}

export interface EntryRow {
  submission_id: string; course_work_id: string; student: string;
  /** 🔴 THE MARK TO TYPE: the reviewed one where a review is in force. The server refuses to
   *  record any other number as entered for a reviewed grade. */
  points: number | null; max_points: number | null;
  agent_points: number | null; reviewed_by: string | null;
  status: string; escalate: boolean;
  alternate_link: string; student_note: string; already_entered: boolean;
}

export interface HealthCard {
  name: string; state: "OK" | "WARN" | "STOP" | "UNKNOWN"; detail: string;
  [k: string]: unknown;
}

export interface ModelOffer {
  id: string; label: string; provider: string; note: string;
}

export interface HarnessOffer {
  id: string; label: string; note: string;
  providers: string[];
  /** ⚠️ A CAVEAT, NOT A REFUSAL. Whether a harness may grade an assignment depends on that
   *  assignment's rubric, which this tab has not chosen — so it explains rather than
   *  disabling a choice it cannot actually evaluate. */
  models: ModelOffer[];
}
/** Which tier a value came from, and what it would fall back to. */
export interface SettingTier { value: unknown; source: "course" | "inherited"; inherited: unknown }

export interface SettingsPayload {
  settings: Record<string, unknown>;
  /** 🔴 PROVENANCE, which `settings` alone cannot carry — it is three layers flattened into
   *  one dict, so nothing downstream could tell a cohort's own decision from the global it
   *  inherits. That is why the console has never been able to show it. */
  tiers: Record<string, SettingTier>;
  // ⚠️ NO `stacks` SINCE 2026-09-27 — the toolchain image comes from the language detected per
  // submission, so there is no picker to feed. (It once sat at the top of this type while the
  // server sent it inside `engines`, and the picker offered nothing: `api<T>()` is an
  // unchecked cast, so tsc could not see it.)
  engines: { harnesses: HarnessOffer[]; efforts: string[] };
  may_edit: boolean;
  notes: Record<string, string>;
  // ⚠️ NO `history` SINCE 2026-10-04: who changed what is the Logs page's (`activity.rows`),
  // which reads only this cohort's changes and the console-wide ones. This list read every
  // cohort's, to anybody who could view one.
}

/** One citation: a file and line in the submission, the text there, and what it is evidence of.
 *  Every file was resolved inside the submission when the grade was made. */
export interface Cite {
  file: string; line: number; quote: string;
  /** The grader's few words on what the line shows ("task 1: the mint is created"). Optional,
   *  from 2026-09-29; a grade made before carries none. */
  shows?: string;
}

/** One shortfall the grader itemised — where some of a criterion's missing points went.
 *  🔴 IT MOVES NO MARK: the score is `earned`, decided by the console. These say why it is not
 *  the weight, and every citation in them was checked like any other. */
export interface LostItem {
  points: number; what: string;
  /** Why it cost that much. */
  detail?: string;
  evidence: Cite[];
  /** The id of the teacher's test these points were lost to, when the grader named one. */
  test?: string;
}

/** The grader's answer to one requirement of a task, and what the console counted it as. */
export interface RequirementResult {
  id: string; text: string;
  /** What the grader answered: met · partly · missing, or "" when it did not answer. */
  verdict: string;
  /** What was counted: the answer, or missing when it had nothing that checked out. */
  counted: "met" | "partly" | "missing";
  value: number;
  /** What it cost, on a required task only — the same number as its lost item. Absent on a grade
   *  made before 2026-10-13, and on optional work, which cannot lose points. */
  lost?: number;
  /** Only citations that checked out. */
  evidence: Cite[];
  /** Why the console counted it differently from the answer. */
  fault?: string;
  detail?: string;
  test?: string;
}

/** Extra credit from optional tasks: it makes up points the required ones lost, never past the
 *  criterion's weight. */
export interface TaskCredit { points: number; from: string[]; reason: string }

/** One task of a criterion scored from its tasks (2026-10-07). */
export interface TaskResult {
  id: string; title: string;
  description?: string;
  counts: "required" | "extra_credit" | "report_only";
  points: number;
  /** Null for a report-only task, which is judged and worth nothing. */
  earned: number | null;
  /** Empty when the task has subtasks; a grade from before subtasks has only these. */
  requirements: RequirementResult[];
  subtasks?: SubtaskResult[];
}
export interface SubtaskResult {
  id: string; title: string; description?: string;
  points: number; earned: number | null;
  requirements: RequirementResult[];
}

/** One of a teacher's tests for an assignment (2026-10-01): in words, as a code file, or
 *  `brief` — the essential tests the grader writes from what the assignment asks. The server
 *  mints `id`, and names a `brief` one; a test the editor adds has no id until it is saved. */
export interface TeacherTest {
  id?: string;
  kind: "words" | "code" | "brief";
  name: string;
  what?: string;
  path?: string;
  content?: string;
  /** The criterion it bears on (2026-10-05). Absent on a test saved before then, which belongs
   *  to `C1` — the server's `teacher_tests.DEFAULT_CRITERION`. */
  criterion?: string;
  /** The part of the assignment it proves, in the teacher's words; may be empty. */
  task?: string;
}

export interface TeacherTestsView {
  version: number;
  items: TeacherTest[];
  saved_by: string;
  saved_at: string | null;
  history: { version: number; saved_by: string; saved_at: string | null; tests: number }[];
  limits: { tests: number; files: number; chars: number; suffixes: string[] };
  may_edit: boolean;
  saved?: boolean;
}

/** What one of the teacher's tests found on one grade. A file is one row; each test function
 *  in it is one of its `runs`. */
export interface TeacherTestResult {
  id: string;
  kind: "words" | "code" | "brief";
  name: string;
  path?: string;
  version: number;
  /** `not_reported` — the grader never ran it, or never said so. */
  status: "ran" | "not_reported";
  /** The criterion it counts under and the task it proves, as the run was given them; absent
   *  on a grade made before 2026-10-05. */
  criterion?: string;
  task?: string;
  runs: { name: string; result: "pass" | "fail" | "absent"; assertion: string; command: string }[];
  /** The test as it ran: the grader's own for one given in words, the adapted file for code. */
  source: string;
  changes: { from: string; to: string; why: string }[];
  /** For code: did the assertions survive, with only the renames the grader reported? */
  integrity: "" | "unchanged" | "changed" | "unverified";
  integrity_detail: string;
  /** The test as the teacher gave it, from the attempt that made this grade: what a test in
   *  words (or the generic one) checks, and the file a code test was given as. */
  what?: string;
  given?: string;
}

/** Points the CONSOLE took off, not the grader: a judgement it zeroed, a criterion never judged,
 *  or a mark rounded down to a whole point — with the reason in a sentence. */
export interface ConsoleLoss { points: number; reason: string }

export interface Report {
  submission_id: string; course_work_id: string; student: string; assignment: string;
  status: string; points: number | null; max_points: number | null; outcome: string;
  escalate: boolean; gradeable: boolean; released: boolean;
  released_at: string | null; released_by: string | null; graded_at: string | null;
  /** 🔴 THE SHAPE THE SERVER ACTUALLY SENDS, verified against `validate_agent.py:310-318`.
   *
   * This used to declare `evidence?: string; why?: string; citation?: string`, and none of
   * those three has existed since the scorer started writing per-criterion rows. The tab read
   * them anyway: `evidence === "test"` was never true, so the meter always said "0 of 0 by the
   * reference suite"; `{c.evidence ?? "?"}` rendered an ARRAY OF OBJECTS as a React child; and
   * the Why column and every citation link were blank, because the fields are called
   * `reasoning` and `evidence[]`.
   *
   * ⚠️ TypeScript could not catch it — nothing compares this interface with the router's
   * return dict, so both sides were internally consistent and disagreed with each other.
   * `validate.py` now gates the pair.
   */
  per_criterion: {
    id: string; weight: number; earned: number | null;
    label?: string;
    /** "judged" | "advisory" | "absent" | "not_judged" — whether the agent produced a verdict
     *  at all, and whether it counted.
     *  🔴 `advisory` IS NOT `judged` WITH A ZERO. "Judged and worth nothing because a teacher
     *  chose that" and "scored zero because its citations did not resolve" are opposite claims
     *  about a student's work, and they share no result word and no arithmetic. */
    result?: string;
    /** Only on an advisory row: what it would have been worth, and what the agent would have
     *  given it. `null` points means the agent did not judge it or its citations failed —
     *  neither of which changes the mark. Nothing sums these. */
    advisory_weight?: number;
    advisory_points?: number | null;
    /** "llm" — how the points were decided. One value today; kept because a second would
     *  change what the number means. */
    source?: string;
    confidence?: number | null;
    /** One or two plain sentences: the verdict and what decided it. Optional, from 2026-09-29. */
    summary?: string;
    /** The whole account, in Markdown from 2026-09-29. Written after reading a repository the
     *  student controls, so it renders without links. */
    reasoning?: string;
    /** Where the agent says it saw it. Every file is resolved inside the submission before
     *  the criterion scores; one that does not resolve scores zero. */
    evidence?: Cite[];
    /** 🔴 WHY IT IS NOT THE WEIGHT. `lost` is the grader's own itemisation; `lost_console` is
     *  what the console took off itself; `lost_unexplained` is what neither accounts for
     *  (negative when the items add up to more than was taken off). All three are the server's
     *  arithmetic — the page adds nothing up. Absent on a grade made before 2026-09-29. */
    lost?: LostItem[];
    lost_console?: ConsoleLoss;
    lost_unexplained?: number;
    /** 🔴 A CRITERION SCORED FROM ITS TASKS (2026-10-07): the grader answered each requirement and
     *  the console added them up, so `earned` is the console's sum and not the grader's number.
     *  `credit` is extra credit from optional tasks, which can make up lost points and never
     *  passes the weight. */
    scored_by?: "tasks";
    tasks?: TaskResult[];
    /** What the assignment is, as the run was given it; context, never marked. */
    task_summary?: string;
    credit?: TaskCredit;
  }[];
  problems: string[];
  /** 🔴 OBJECTS, NOT STRINGS. This said `string[]` while the server sent `{kind, detail}`, and
   *  no page rendered them — so a flag the prompt says exists "so the instructor notices it"
   *  reached nobody. `suggested_profile` rides on a `profile_mismatch`. */
  flags: { kind: string; detail: string; suggested_profile?: string }[];
  fixes_applied: { what?: string; why?: string }[];
  /** 🔴 WHAT THE GRADER DID, read from the envelope already stored on the attempt — not a
   *  second copy that could disagree with it. Optional: an older row, or a malformed
   *  envelope, renders no section rather than failing the page. */
  agent_account?: {
    summary?: string; outcome?: string; turns?: number; duration_s?: number;
    /** What it EXECUTED and what happened. The agent uses this for build and type-check
     *  commands as well as test runs, which is why `result` is an outcome and not a verdict. */
    tests?: { name?: string; result?: string; assertion?: string }[];
    /** What it would ADD. Never scored — a model that writes its own exam marks its own
     *  homework — and shown to the teacher beside its reasoning. */
    suggested_tests?: { name?: string; what_it_checks?: string; tool?: string;
                        why?: string }[];
  };
  /** The grader's one-line notes on files the grade cites, for the By file view. A note on a
   *  file the grade does not cite is never sent. */
  file_notes: { file: string; note: string }[];
  /** The teacher's tests and what each found — teacher-facing only (2026-10-01). */
  teacher_tests: TeacherTestResult[];
  student_note: string; teacher_note: string;
  /** `commit` is the whole hash the grade is of; "" on a grade made before runs were pinned. */
  repo: {
    url: string; ref: string; subpath: string; commit: string;
    /** The submodules the run fetched, each at the commit the student's repository pins. Absent
     *  from a server older than 2026-10-07. */
    submodules?: { path: string; url: string; sha: string }[];
  };
  alternate_link: string;
  grader: Record<string, unknown>;
  /** 🔴 USAGE, NOT A PRICE (2026-10-03): tokens, time and the subscription's own figures. A
   *  gateway model's spend is the one dollar figure, because there it is real. */
  usage: { tokens: Tokens | null; models: string[]; duration_ms: number | null;
           duration_api_ms: number | null; num_turns: number | null;
           subscription: UsageWindows;
           /** The run went through the gateway; decided by the server, never mapped here. */
           gateway: boolean; gateway_usd: number | null };
  attribution: { verdict: string | null; detail: Record<string, unknown>;
                 model_attribution: unknown; cache_hit: boolean | null;
                 tokens: Record<string, unknown>; tokens_unattributed: Record<string, unknown> };
  permission_denials: { tool_name?: string; tool_input?: unknown }[];
  /** 🔴 HOW IT GOT THERE, STEP BY STEP (2026-10-08) — `{}` for a run made before runs kept one. */
  run_log: RunLog;
  num_turns: number | null; duration_ms: number | null;
  attempt: { id: string; wm_job_id: string; attempt_no: number; requested_by: string;
             envelope_received_at: string | null; processed_at: string | null } | null;
  classroom_writes: { kind: string; points: number | null; status: string; actor: string;
                      error: string; at: string | null }[];
  may_release: boolean; write_allowed: boolean;
  /** The piece of work, as opposed to `assignment`, which is the rubric it was marked against. */
  assignment_title: string;
  /** 🔴 THE REVIEW IN FORCE, or null. With one, a release posts its total, not `points`. */
  review: GradeReviewView | null;
  /** `can_release` — a review decides the number that reaches a student. */
  may_review: boolean;
  /** `can_edit_rubrics` — choosing the language decides what a mark is made of. */
  may_set_language: boolean;
  /** `can_run_grading` — running it again spends. A separate press from choosing. */
  may_run: boolean;
  /** 🔴 WHICH LANGUAGE THIS MARK WAS MADE IN, AND WHO SAID SO (2026-09-27). Empty `profile` on a
   *  grade made before languages were detected. `evidence` is marker paths — shown to the
   *  teacher, never to the model. `override` is what the NEXT run uses, which may differ. */
  language: {
    profile: string; label: string; version: number; source: string; evidence: string[];
    override: { profile: string; label: string; set_by: string; set_at: string | null;
                applies: boolean } | null;
    options: { id: string; label: string }[];
  };
  /** The server's list, so the page cannot offer a reason the database refuses. */
  review_reasons: string[];
}

/** A teacher's verdict on one criterion, as stored. `agree` always carries the agent's own
 *  number; `adjust` carries the teacher's and a reason. */
export interface ReviewVerdict {
  id: string; verdict: "agree" | "adjust"; points: number; agent_points: number | null;
  reason: string; note: string;
}

export interface GradeReviewView {
  id: string; reviewer: string; created_at: string; points: number;
  agent_points: number | null; note: string; calibration: boolean;
  criteria: ReviewVerdict[];
}

/** One criterion as sent. An undecided criterion is left OUT rather than sent blank, so the
 *  server names it ("the review leaves out C2") instead of refusing a malformed body. */
export interface ReviewCriterionInput {
  id: string; verdict: "agree" | "adjust";
  points?: number | null; reason?: string; note?: string;
}

const c = (id: string) => `/courses/${encodeURIComponent(id)}`;

export const getCohort      = (id: string) => api<Cohort>(`${c(id)}/cohort`);
export const getAssignments = (id: string) =>
  api<{ assignments: Assignment[] }>(`${c(id)}/assignments`);
export interface RubricVerification {
  // `ok` — the console's copy equals what the agent will read.
  // `unavailable` — the check could not run. Reported as itself, NEVER as agreement.
  // `mismatch` — the console would score against a rubric the agent never saw.
  state: "ok" | "unavailable" | "mismatch";
  checked: string[]; mismatched: string[]; unavailable: string[];
}
export const getRubrics = (id: string) =>
  api<{ rubrics: RubricInfo[]; verified: RubricVerification; default_rubric_id: string;
        profiles: ProfileInfo[] }>(
    `${c(id)}/rubrics`);
export const getSubmissions = (id: string, cw: string) =>
  api<{ rows: SubmissionRow[]; counts: Record<string, unknown> }>(
    `${c(id)}/assignments/${encodeURIComponent(cw)}/submissions`);
/** 🔴 `scope: "all"` READS EVERY COHORT THIS TEACHER MAY VIEW, and the server bounds that —
 *  `deps.viewable_ids`, the same query `/me` builds the picker from. The page asks for the
 *  wide read and does the cohort filtering itself, because the design's near-miss row has to
 *  show what a filter is HIDING and that is not knowable from a narrowed request. */
export const getRuns = (id: string, scope: "" | "all" = "") =>
  api<{ rows: RunRow[]; counts: Record<string, number>; scope: string; course_id: string }>(
    `${c(id)}/runs${scope ? `?scope=${scope}` : ""}`);
/** One teacher review and what became of it, as `domain/reviews.py` reads it (2026-10-15). */
export interface ReviewCriterion {
  id: string; label: string; weight: number;
  /** What the agent gave it; null where it never judged it (it scored 0). */
  agent_points: number | null; agent_result: string;
  verdict: "agree" | "adjust"; points: number; reason: string; note: string;
}
/** in_force — counts now · replaced — a later review of the same grade took over ·
 *  regraded — the grade it reviewed was redone · archived — a setup change archived it. */
export type ReviewState = "in_force" | "replaced" | "regraded" | "archived";
export interface ReviewRow {
  id: string; course_id: string; cohort: string; submission_id: string; course_work_id: string;
  student: string; assignment_title: string; reviewer: string; created_at: string | null;
  calibration: boolean; note: string; points: number; max_points: number;
  agent_points: number | null; moved: boolean; state: ReviewState; counted: boolean;
  replaced_by: { id: string; reviewer: string; created_at: string | null } | null;
  now: { graded_at: string | null; points: number | null; reviewed: boolean } | null;
  grade: { graded_at: string | null; model: string; effort: string; commit_sha: string;
           rubric_sha256: string };
  criteria: ReviewCriterion[];
}
/** Over the reviews that count — each grade once, by its last review. Change is the teacher's
 *  points minus the agent's. Worked out by the server, never by the page. */
export interface ReviewGroup { reviewed: number; all_agreed: number; mean_change: number | null }
export interface ReviewAgreement {
  few: number; grades: number; criteria: number; criteria_agreed: number;
  mean_change: number | null; calibration: number; moved_grades: number;
  by_criterion: { id: string; label: string; reviewed: number; agreed: number;
                  mean_change: number | null; main_reason: { reason: string; n: number } | null }[];
  by_assignment: (ReviewGroup & { course_id: string; course_work_id: string; title: string;
                                  cohort: string })[];
  by_setting: (ReviewGroup & { model: string; effort: string })[];
  reasons: { reason: string; n: number }[];
}
/** 🔴 `scope: "all"` READS EVERY COHORT THIS TEACHER MAY VIEW, bounded by the server as on Runs —
 *  and the figures follow the scope, so the page asks again rather than filtering them. */
export const getReviews = (id: string, scope: "" | "all" = "") =>
  api<{ rows: ReviewRow[]; agreement: ReviewAgreement; scope: string; course_id: string }>(
    `${c(id)}/reviews${scope ? `?scope=${scope}` : ""}`);
/** Hand a paid run back to the collector to finalise its stored envelope again. Starts no run. */
/** 🔴 WHAT A RUN DID, STEP BY STEP (2026-10-08): the jail's run log as `domain/run_log.py`
 *  re-types it — one row per tool call, how the run ended against its limits, and the scripts
 *  it left in ./sandbox. A teacher's view only: a step can carry the student's build output. */
export interface RunLogStep {
  turn: number | null; tool: string; what: string; chars: number | null;
  status: "ok" | "refused" | "error" | "no_result";
  /** The CLI's kind of refusal, on a refused step. */
  reason: string;
  /** The tail of a sandbox call's output, or a failed step's message. */
  output: string;
}
export interface RunLog {
  limits?: { max_turns: number | null; timeout_s: number | null };
  ended?: { subtype: string; terminal_reason: string; turns: number | null; exit: number | null;
            at_limit: boolean; sentence: string };
  steps_total?: number;
  steps?: RunLogStep[];
  counts?: { ok: number; refused: number; error: number; no_result: number };
  scripts?: { name: string; chars: number | null; content: string }[];
}
export const getRunLog = (id: string, attemptId: string) =>
  api<{ attempt_id: string; outcome: string; error: string; collected: boolean;
        run_log: RunLog }>(`${c(id)}/runs/${encodeURIComponent(attemptId)}/log`);
export const reprocessRun = (id: string, attemptId: string) =>
  api<{ reprocess: boolean; attempt_id: string; retry_at: string; note: string }>(
    `${c(id)}/runs/${encodeURIComponent(attemptId)}/reprocess`, { method: "POST" });
export const getStudents    = (id: string) =>
  api<{ rows: StudentRow[]; roster_names_available: boolean; note: string;
        build_log_window_days: number; build_log_window_end: string;
        last_synced: string | null;
        /** Whether the Classroom bot carries `classroom.profile.emails`. */
        email_scope: boolean;
        classroom_fetched_at: string; classroom_age_seconds: number;
        /** 🔴 HOW SIGN-INS ARE MATCHED TO THIS ROSTER (`domain/identity.py`): by email, or by
         *  the Google account where no roster row carries the address. `with_email` is how many
         *  rows Classroom gave an address at all; `disagree` how many have an address that is
         *  not the one their own account signs in with. If `signed_in` climbs while `matched`
         *  stays 0, the project form refuses every student with a sentence about not being
         *  enrolled.
         *  ⚠️ `signed_in` IS CONSOLE-WIDE, not per cohort — it counts every applicant row —
         *  so it must never be labelled as this cohort's. `matched` is the per-cohort number. */
        identity: Identity;
        counts: Record<string, number> }>(`${c(id)}/students`);
/** 🔴 `waiting` IS THE MARKS STILL TO TYPE — every row listed minus the ones already entered —
 *  and it is the number the Overview counts too. `total` includes the entered rows, which the
 *  list still shows as the record of what was typed. */
export const getEntryQueue  = (id: string) =>
  api<{ rows: EntryRow[];
        counts: { total: number; waiting: number; review: number; entered: number } }>(
    `${c(id)}/entry-queue`);
/** Which commit the server was built from: `/api/health`, which needs no session and reads no
 *  database. `"unknown"` for an image CI did not build. The per-cohort Health page is
 *  `getHealth`, below — a different route with a different answer. */
export const getBuild = () => api<{ commit: string }>("/health");
export const getHealth      = (id: string) =>
  api<{ course: { id: string; name: string }; generated_at: string; cards: HealthCard[];
        /** Whether this reader may lift the usage pause by hand; the route decides again. */
        may_clear_usage_limit: boolean }>(
    `${c(id)}/health`);
/** Lift the credential's usage pause by hand. Estate-wide: every cohort resumes. */
export const clearUsageLimit = (id: string, route: string) =>
  api<{ cleared: boolean; route: string; resets_at: string; note: string }>(
    `${c(id)}/health/usage-limit/clear`, { method: "POST", body: JSON.stringify({ route }) });
export const getSettings = (id: string) => api<SettingsPayload>(`${c(id)}/settings`);

export interface PromptLayer {
  id: string; title: string; editable: boolean; why: string; setting: string; text: string;
}
export interface PromptPayload {
  layers: PromptLayer[];
  first_editable: number;
  may_edit: boolean;
  // 🔴 ONE DOCUMENT. This was `{measured, suiteless}` — two method layers chosen
  // by a flag dispatch hardcoded, so the tab's default preview showed an Anchor
  // build recipe no grading run had ever received.
  preview: { prompt: string };
  sha256: { prompt: string };
  manifest: { id: string; title: string; editable: boolean; chars: number; sha256: string }[];
  note: string;
}
export const getPrompt = (id: string) => api<PromptPayload>(`${c(id)}/prompt`);

export interface RubricDetail {
  id: string; sha256: string; body: unknown;
  suiteless: boolean; has_measured_criteria: boolean;
  may_edit: boolean; affected: number; cost: string;
}
export const getRubric = (id: string, rid: string) =>
  api<RubricDetail>(`${c(id)}/rubrics/${encodeURIComponent(rid)}`);

/** A rubric family as its editor needs it (owner, 2026-09-25).
 *
 * 🔴 NOT UNDER `/courses/`, AND THAT IS THE POINT. A rubric family is not a property of a
 * cohort: editing one changes every assignment bound to it, in every cohort, at once. A
 * per-cohort route would mean "may edit the estate's rubrics, but only from over here", which
 * is not a rule this console could enforce. */
export interface RubricReach {
  assignments: number;
  cohorts: { id: string; name: string }[];
  graded_under_current: number;
  current_version: number;
  /** "file" | "console" — whether the live body came from the vendored file or from an edit
   *  made here. It is what stops the next deploy overwriting somebody's work. */
  current_source: string;
  /** The shipped file has moved underneath a console edit. The edit stands — reverting it is
   *  the defect — but the divergence has to be visible or nobody resolves it. */
  differs_from_shipped: boolean;
}
export interface RubricVersionRow {
  version: number; sha256: string; source: string;
  created_by: string | null; created_at: string; note: string; criteria: number;
}
export interface AdminRubric {
  id: string; body: unknown; sha256: string;
  reach: RubricReach; versions: RubricVersionRow[];
}
export const getAdminRubric = (rid: string) =>
  api<AdminRubric>(`/admin/rubrics/${encodeURIComponent(rid)}`);
export const saveAdminRubric = (
  rid: string, payload: { body: unknown; note?: string; confirm?: boolean }) =>
  api<{ preview?: boolean; saved?: boolean; version?: number; sha256?: string;
        reach: RubricReach; note?: string }>(
    `/admin/rubrics/${encodeURIComponent(rid)}`,
    { method: "PUT", body: JSON.stringify(payload) });
/** One stored body, so a mis-save has a way back that is not re-typing the rubric.
 *
 * ⚠️ IT LOADS, IT DOES NOT REVERT. Saving what it returns makes a NEW version — going back is
 * itself an act with an actor and a time, not a deletion of what happened in between. */
export const getAdminRubricVersion = (rid: string, version: number) =>
  api<{ version: number; body: unknown; sha256: string; source: string;
        created_by: string | null; created_at: string; note: string }>(
    `/admin/rubrics/${encodeURIComponent(rid)}/versions/${version}`);

/** A language profile as its editor needs it — owner-only, like a rubric family, because it
 *  reaches every submission in that language in every cohort. */
export const getAdminProfile = (pid: string) =>
  api<AdminRubric>(`/admin/profiles/${encodeURIComponent(pid)}`);
export const saveAdminProfile = (
  pid: string, payload: { body: unknown; note?: string; confirm?: boolean }) =>
  api<{ preview?: boolean; saved?: boolean; version?: number; sha256?: string;
        reach: RubricReach; note?: string }>(
    `/admin/profiles/${encodeURIComponent(pid)}`,
    { method: "PUT", body: JSON.stringify(payload) });
export const getAdminProfileVersion = (pid: string, version: number) =>
  api<{ version: number; body: unknown; sha256: string; source: string;
        created_by: string | null; created_at: string; note: string }>(
    `/admin/profiles/${encodeURIComponent(pid)}/versions/${version}`);

/** Grade this submission as a named language from its next run — or `""` to detect again.
 *  Writes no grade and archives nothing; the mark on screen keeps the language it was made in. */
export const setSubmissionProfile = (id: string, sid: string, profile: string, note = "") =>
  api<{ override: { profile: string; set_by: string; set_at: string; applies: boolean } | null }>(
    `${c(id)}/submissions/${encodeURIComponent(sid)}/profile`,
    { method: "PUT", body: JSON.stringify({ profile, note }) });
export const getReport      = (id: string, sid: string) =>
  api<Report>(`${c(id)}/submissions/${encodeURIComponent(sid)}/report`);

/** 🔴 `scope: "course"` IS WHAT REACHES THE SECOND TIER. Absent writes the GLOBAL row, which
 *  is what this route has always done and what every other caller means — the cohort tier is
 *  a deliberate act with its own switch on the page. */
export const putSettings = (id: string, changes: Record<string, unknown>,
                            scope: "" | "course" = "") =>
  api<{ applied: Record<string, unknown>; settings: Record<string, unknown>;
        tiers: Record<string, SettingTier> }>(
    `${c(id)}/settings${scope ? `?scope=${scope}` : ""}`,
    { method: "PUT", body: JSON.stringify(changes) });

/** 🔴 A RESET DELETES THE COHORT'S ROW rather than copying the global value into it, so the
 *  cohort follows the global *forever after* instead of silently stopping the next time it
 *  changes. That is why it is its own route and not a value meaning "no value". */
export const resetSettings = (id: string, keys: string[]) =>
  api<{ cleared: string[]; settings: Record<string, unknown>;
        tiers: Record<string, SettingTier> }>(
    `${c(id)}/settings/reset`, { method: "POST", body: JSON.stringify({ keys }) });

// 🔴 ONE CALL CARRYING A LIST, NEVER N CALLS IN A LOOP. Bulk enqueue is allowed where bulk
// release is not — every no-bulk rule names the same harm, a claim reaching a student, and
// grading reaches nobody. But N calls would make the set that runs differ from the set that
// was asked for on any partial failure.
/** What happened to ONE submission when Run was pressed.
 *
 * 🔴 PER SUBMISSION, BECAUSE THE ANSWER DIFFERS PER SUBMISSION. This endpoint used to return
 * one fixed sentence — "Requested, not started…" — on every call, describing a past incident
 * and reflecting nothing about the queue, the caps or whether anything started. `detail` is
 * now the dispatcher's own refusal text, which already names the reason and the remedy.
 */
export interface QueueResult {
  submission_id: string;
  status: "started" | "queued" | "refused" | "held";
  detail: string;
  reason?: string;
  attempt_id?: string;
  wm_job_id?: string;
  engine?: string;
}

export const queueGrade = (id: string, course_work_id: string, submission_ids: string[]) =>
  api<{ queued: number; submission_ids: string[]; started: number;
        results: QueueResult[]; note: string }>(
    `${c(id)}/queue`, { method: "POST",
                        body: JSON.stringify({ course_work_id, submission_ids }) });

/** Take queued runs out of the queue before they start (2026-10-07). No `submission_ids` takes
 *  every queued run on the assignment out; a run already in progress is never cancelled. */
export const cancelQueued = (id: string, course_work_id: string, submission_ids?: string[]) =>
  api<{ cancelled: string[]; started: string[]; not_queued: string[]; note: string }>(
    `${c(id)}/queue/cancel`, { method: "POST",
                               body: JSON.stringify({ course_work_id,
                                                      submission_ids: submission_ids ?? null }) });

export const getTeacherTests = (id: string, cw: string) =>
  api<TeacherTestsView>(`${c(id)}/assignments/${encodeURIComponent(cw)}/tests`);
export const putTeacherTests = (id: string, cw: string,
                                body: { items: TeacherTest[]; base_version: number }) =>
  api<TeacherTestsView>(`${c(id)}/assignments/${encodeURIComponent(cw)}/tests`,
                        { method: "PUT", body: JSON.stringify(body) });

/** Tasks the gateway model found in the brief, with even weights. A PROPOSAL: nothing is saved
 *  until it goes out through the binding, whose preview says what that re-grades. */
export interface TasksProposal { tasks: TaskDraft[]; summary: string; model: string; note: string;
                                 points: number }
export const extractTasks = (id: string, cw: string) =>
  api<TasksProposal>(`${c(id)}/assignments/${encodeURIComponent(cw)}/tasks/extract`,
                     { method: "POST" });

export const putBinding = (id: string, cw: string, body: Record<string, unknown>) =>
  api<Record<string, unknown>>(`${c(id)}/assignments/${encodeURIComponent(cw)}/binding`,
                               { method: "PUT", body: JSON.stringify(body) });

// 🔴 PUBLISHING NOTIFIES EVERY STUDENT ON THE COURSE and there is no unpublish that
// un-notifies, so `confirm=false` is a preview that writes nothing — the same shape as
// release, for the same reason.
export const publishAssignment = (id: string, cw: string, confirm: boolean) =>
  api<Record<string, unknown>>(
    `${c(id)}/assignments/${encodeURIComponent(cw)}/publish`,
    { method: "POST", body: JSON.stringify({ confirm }) });

/** Fetch the cohort roster now. `can_view` — it writes only names already on this screen. */
export const syncRoster = (courseId: string) =>
  api<Record<string, unknown>>(`/courses/${courseId}/roster/sync`, { method: "POST" });

// ⚠️ AN EMPTY HANDLE REMOVES THE LINK, and the posts already collected stay: they are a
// record of what was checked, and deleting them because somebody fixed a typo would rewrite
// a teacher's own history.
export interface BuildLogWeek {
  week_start: string; total: number; on_topic: number; off_topic: number;
  unclassified: number;
  // 🔴 ON-TOPIC POSTS ONLY. Off-topic posts are counted and never linked — a clickable list
  // of them is the characterisation the schema refuses, delivered by reference instead of
  // by value. See `domain/buildlog.py:detail`.
  links: { url: string; at: string }[];
}

export const getBuildLog = (courseId: string, studentId: string) =>
  api<{ handle: string; kind: string; resolved: boolean; unavailable_reason: string;
        added_by: string; added_at: string;
        weeks: BuildLogWeek[]; total_known: number; undated: number;
        stale: number; unknown_config: number }>(
    `/courses/${courseId}/students/${studentId}/build-log`);

export interface SweepPreview {
  preview?: boolean; would_run?: boolean; reason?: string; days: number;
  students?: number; est_usd?: number;
  ran?: boolean; started?: string; collected?: number; skipped?: string;
}

// ⚠️ PREVIEW-THEN-CONFIRM. Without `confirm` this writes nothing and returns the price and
// the number of students it would sweep; re-judging a month for a whole cohort is real money.
// `days` is what makes one route serve both "sweep now" and "re-classify the last N days" —
// the store upserts on (student, source, post id) and rewrites the verdict, so a wider window
// re-labels rather than duplicates.
export const sweepBuildLog = (courseId: string, body: { days?: number; confirm?: boolean }) =>
  api<SweepPreview>(`/courses/${courseId}/build-log/sweep`,
                    { method: "POST", body: JSON.stringify(body) });

export const setStudentLink = (courseId: string, studentId: string, handle: string,
                               kind = "x") =>
  api<{ ok: boolean; handle: string; resolving?: boolean; removed?: boolean }>(
    `/courses/${courseId}/students/${studentId}/link`,
    { method: "PUT", body: JSON.stringify({ handle, kind }) });

export const poll = (id: string, body: { rescan?: boolean; auto_grade?: boolean }) =>
  api<Record<string, unknown>>(`${c(id)}/poll`, { method: "POST", body: JSON.stringify(body) });

// 🔴 `api.release` IS CALLED FROM EXACTLY ONE PLACE AND NEVER INSIDE A LOOP. validate.py
// asserts both. A release is the only call here that reaches a student.
export const release = (id: string, sid: string, confirm: boolean) =>
  api<{ preview?: boolean; released?: boolean; points?: number;
        /** 🔴 THE NUMBER THE PUBLISH BUTTON NAMES, from the server — never recomputed here,
         *  because a label saying 69 over a write of 75 is the defect a preview exists to
         *  prevent. */
        would_release?: { points: number; max_points: number | null;
                          reviewed_by: string | null; agent_points: number | null } }>(
    `${c(id)}/submissions/${encodeURIComponent(sid)}/release`,
    { method: "POST", body: JSON.stringify({ confirm }) });

/** Agree or Adjust on every criterion. Writes nothing to Classroom; a release then posts the
 *  reviewed total. `graded_at` is the report's own — a re-grade since is refused with 409. */
export const reviewGrade = (id: string, sid: string, body: {
  graded_at: string; criteria: ReviewCriterionInput[]; note: string; calibration: boolean;
  confirm: boolean;
}) =>
  api<{ preview?: boolean; reviewed?: boolean; review_id?: string; points: number;
        agent_points: number | null }>(
    `${c(id)}/submissions/${encodeURIComponent(sid)}/review`,
    { method: "POST", body: JSON.stringify(body) });

export const markEntered = (id: string, body: Record<string, unknown>) =>
  api<Record<string, unknown>>(`${c(id)}/entered`,
                               { method: "POST", body: JSON.stringify(body) });


// ── the dashboard ───────────────────────────────────────────────────────────────

/** One thing that needs a person, with the page it is about.
 *
 * 🔴 DERIVED ON THE SERVER, so the page cannot disagree with the pipeline about whether
 * money was lost. `page` is checked against `routes.ts` by a Python test. */
export interface AttentionItem {
  kind: string;
  severity: "STOP" | "WARN";
  count: number;
  title: string;
  detail: string;
  page: string;
  tab?: string;
  /** A status word the view should open filtered to — the one the item counts. */
  status?: string;
  course_work_id?: string;
}

export interface OverviewAssignment {
  course_work_id: string;
  /** Empty until the Assignments page has listed once — that route writes the cache. */
  title: string;
  due: string;
  cached: boolean;
  /** Says what THIS assignment requires beyond the base — extras, its own credit lines or
   *  notes, guidance. Every assignment inherits the one base since 2026-09-27, so "names a
   *  rubric" stopped meaning anything; this is `rubric.is_specific`, shared with the Binder. */
  bound: boolean;
  /** 🔴 THE EFFECTIVE VALUES. A binding beats the cohort setting, and the UI once taught
   *  the owner the opposite. */
  effective_engine: string;
  effective_auto_grade: boolean;
  ours: { graded: number; ready: number; review: number; held: number;
          released: number; entered: number };
  queue: { parked: Record<string, number> };
}

export interface Overview {
  course: { id: string; name: string; write_allowed: boolean };
  generated_at: string;
  can_release: boolean;
  grades: { total: number; ready: number; review: number; held: number;
            released: number; entered: number };
  assignments: OverviewAssignment[];
  queue: { queued: number; dispatched: number;
           parked_by_reason: Record<string, number>; undrained_requests: number };
  runs: { running: number; uncollected: number; stored_nothing_7d: number;
          failed_7d: number; refused_7d: number; recorded_7d: number;
          last_finished_at: string | null };
  /** 🔴 USAGE, NOT A PRICE (2026-10-03). The Health card's own reading — the figures the poller
   *  pauses on and the count dispatch parks on. Social activity keeps its dollars. */
  usage: {
    grading: UsageGrading;
    /** `measured` is how many of the day's runs reported anything; `tokens` and `minutes` are
     *  theirs alone. A run still going has measured nothing, and is not a zero. */
    series: { day: string; runs: number; measured: number; tokens: number; minutes: number }[];
    build_log: { today_est_usd: number; cap_usd: number };
  };
  /** `review` counts by status word: a reviewed grade is escalated and waits for nobody. */
  /** Marks not yet typed into Classroom: all of them, those still in review, and the Ready ones
   *  — what the Ready filter lists and the attention item counts (2026-10-13). */
  entry: { waiting: number; review: number; ready: number };
  build_log: { enabled: boolean; state: string; last_collected_at: string | null;
               window_end: string | null; window_days: number; linked: number;
               on_topic: number; off_topic: number; unclassified: number;
               today_est_usd: number; cap_usd: number };
  roster: { students: number; named: number; last_synced: string | null; note: string };
  attention: AttentionItem[];
}

/** What Classroom says is in, per assignment. Its own route because it is the one Google
 *  call on the Overview and it may be slow or fail without taking the page with it. */
export interface Progress {
  by_assignment: Record<string, {
    turned_in: number; returned: number; submitted: number;
    by_state: Record<string, number>; marked_in_classroom: number;
  }>;
  totals: { turned_in: number; returned: number; submitted: number;
            marked_in_classroom: number; assignments: number };
  fetched_at: string;
  /** ⚠️ HOW OLD, NOT JUST WHEN. A cache that cannot say its age turns a stopped poller into
   *  confident wrong numbers, so the page states it beside the figures. */
  age_seconds: number;
  listed: number;
  from_cache: boolean;
}

export interface ActivityRow {
  /** Which cohort the event belongs to. Empty for a GLOBAL settings change, which belongs to
   *  no cohort and must not be borrowed by whichever one you are viewing. */
  course_id: string;
  at: string;
  /** An email, or the literal "sweep" for a run nobody asked for. */
  actor: string;
  kind: string;
  /** A sentence built from an allowlist of `detail` keys — never the blob. */
  text: string;
  page: string;
  course_work_id: string;
  submission_id: string;
}

export const getOverview = (id: string) => api<Overview>(`${c(id)}/overview`);

/**
 * 🔴 WHAT IS RUNNING, READ FROM THE DATABASE ALONE (2026-10-06): the feed the console polls so a
 * teacher never has to refresh to see a run land. A run's `stage` is its attempt's, never the
 * queue's word — `dispatched` stays on a queue entry after its run has ended.
 */
export type LiveStage = "starting" | "waiting" | "grading" | "checking" | "held";
export interface LiveMark { points: number | null; max_points: number | null; status: string }
export interface LiveRun {
  attempt_id: string; submission_id: string; course_work_id: string;
  student: string; assignment_title: string; stage: LiveStage;
  /** When the console sent it. */
  started_at: string | null;
  /** When a grader took it — Windmill's own start, read by the collector (`0053`). Null while it
   *  waits, and on a run from before. ⚠️ Optional: absent from a server older than the page. */
  running_since?: string | null;
  envelope_received_at: string | null;
  retry_finalise_at: string | null; requested_by: string;
  /** A run over a mark that is still stored — it is the grade until this one lands. */
  regrade: boolean; previous: LiveMark | null;
  /** Whether Reprocess applies — the Runs page's own rule (`refinalise.refusal`), never a copy. */
  reprocessable: boolean;
}
export interface LiveQueued {
  submission_id: string; course_work_id: string; student: string; assignment_title: string;
  /** 1 is next: the poller's own order, a pressed Run ahead of everything nobody asked for. */
  position: number; requested: boolean; requested_by: string; since: string | null;
  reason: string;
}
export type LiveOther =
  | { kind: "team_grouping"; id: string; status: string; requested_at: string | null;
      started_at: string | null; eligible: number; requested_by: string }
  | { kind: "social_sweep"; job_kind: string; started_at: string | null };
export interface LiveFinished extends LiveMark {
  attempt_id: string; submission_id: string; course_work_id: string;
  student: string; assignment_title: string;
  outcome: "recorded" | "refused" | "failed" | "abandoned" | "limited" | "held";
  finished_at: string | null; retry_finalise_at: string | null;
  /** The first sentence of why it ended without a grade; the whole of it is on Runs. */
  detail: string;
}
/** A team grouping that ended in the last hour, and whether its run made a plan. */
export interface LiveGrouped {
  id: string; outcome: "ready" | "failed"; finished_at: string | null; eligible: number;
  requested_by: string;
  /** The first sentence of why it failed; empty when it made a plan. */
  detail: string;
}
export interface LiveFeed {
  /** The server's clock, so a timer in the browser is offset by it rather than trusting the
   *  viewer's own. */
  now: string;
  runs: LiveRun[]; queued: LiveQueued[]; other: LiveOther[];
  /** The last hour's endings, newest first. */
  finished: LiveFinished[];
  /** The last hour's team groupings, newest first. ⚠️ Absent from a server older than the page —
   *  for the minute of a deploy, the page and the server can be either side of it. */
  grouped?: LiveGrouped[];
  classroom_checked_at: string | null;
  counts: { grading: number; queued: number; other: number };
}
export const getLive = (id: string) => api<LiveFeed>(`${c(id)}/live`);
export const getProgress = (id: string, refresh = false) =>
  api<Progress>(`${c(id)}/progress${refresh ? "?refresh=true" : ""}`);
export const getActivity = (
  id: string,
  opts: { limit?: number; kind?: string; courseWorkId?: string; before?: string;
          scope?: "" | "all" } = {},
) => {
  const q = new URLSearchParams();
  if (opts.limit) q.set("limit", String(opts.limit));
  if (opts.kind) q.set("kind", opts.kind);
  if (opts.courseWorkId) q.set("course_work_id", opts.courseWorkId);
  if (opts.before) q.set("before", opts.before);
  if (opts.scope) q.set("scope", opts.scope);
  const s = q.toString();
  return api<{ rows: ActivityRow[]; kinds: { id: string; label: string }[]; note: string;
               scope: string; course_id: string }>(
    `${c(id)}/activity${s ? `?${s}` : ""}`);
};

// ── the two rounds ──────────────────────────────────────────────────────────────
//
// 🔴 TWO ROUNDS AND ONLY ONE OF THEM GROUPS. `/applications` is the INTAKE round — who should
// join this cohort — and it builds no teams. `/projects` is what a cohort that is already
// RUNNING fills in, and it is the only thing a team is ever built from. The Applicants page
// shows the first; the Students page's Teams tab shows the second.
//
// 🔴 TWO PRINCIPALS, TWO SURFACES. Everything under `/api/apply` is the STUDENT's own view and
// resolves a student session; everything under `/api/courses/:id/…` is the teacher's and
// resolves a teacher session. A student page must never call a teacher fetcher: the server
// answers 401, but the reason the two are separated here is so that is obvious while writing
// the page rather than while reading a bug report.
//
// ⚠️ THE STUDENT TYPES CARRY NO JUDGEMENT, and that is deliberate rather than an oversight.
// There is no `level`, no `rationale`, no teammate email — the server does not send them, and
// a page cannot render what it was never given.

export type FormState = "off" | "open" | "closed";
export type ApplicationStatus =
  | "received" | "withdrawn" | "team_assigned" | "not_assigned" | null;

/**
 * One question on the APPLICATION form, which a teacher writes (2026-10-13).
 *
 * 🔴 `id` IS SERVER-MINTED AND ANSWERS LIVE UNDER IT, as on the project form: rewording a
 * question keeps every answer given to it, and a new question never takes a retired one's id.
 * ⚠️ `role` IS HOW THE CONSOLE FINDS A NAME OR A DISCORD NAME ON A FORM WHOSE LABELS A TEACHER
 * WROTE. It sits on a `text` question only, at most once each, and the server copies that
 * answer onto a column a teammate's page reads.
 */
export interface IntakeQuestion {
  id: string;
  kind: "text" | "link" | "choice" | "timezone" | "matrix" | "scale";
  label: string;
  hint: string;
  required: boolean;
  role: "" | "name" | "discord_username" | "discord_user_id";
  /** A `choice` or `timezone`'s list. */
  options?: string[];
  /** A `choice` that also takes a typed answer ("Other…"). */
  other?: boolean;
  max?: number;
  /** A `matrix`: one level per row, from the same list. */
  rows?: string[];
  levels?: string[];
  /** A `scale`, `lowest` to `highest`, with the two ends named. */
  lowest?: number;
  highest?: number;
  low?: string;
  high?: string;
}

/** One answer: text, a scale's number, or a grid's `{row: level}`. */
export type IntakeAnswer = string | number | Record<string, string>;
/** An application's answers, keyed by question id. Nothing here knows what `q8` means; the
 *  form's questions do. */
export type ApplicationAnswers = Record<string, IntakeAnswer>;

export interface ApplyTeammate {
  display_name: string;
  /** 🔴 ONLY ANSWERS THE TEACHER MARKED `share`, as `{q, a}`. It defaults off, so nothing
   *  about a classmate reaches you unless somebody decided it should. */
  shared: { q: string; a: string }[];
  /** How to reach them, from THEIR APPLICATION — absent for somebody who joined the cohort
   *  without applying through this console. An empty string reads as one typed wrong, so the
   *  server omits the key entirely rather than sending one. */
  discord_username?: string;
}

export interface ApplyCohortView {
  course: { id: string; name: string };
  /** 🔴 NULL ON A COHORT THAT IS ALREADY RUNNING. Such a cohort has no application form at
   *  all, and treating that as an error is what made the whole page vanish for a legitimately
   *  enrolled student. It is the ordinary shape of the second round. */
  form: null | {
    state: FormState;
    intro: string;
    /** 🔴 THIS COHORT'S QUESTIONS, which a teacher writes since 2026-10-13. The page draws
     *  what the server sends — a client and a server disagreeing about an option list is how
     *  somebody is refused for picking exactly what they were shown. */
    questions: IntakeQuestion[];
    closes_at: string;
  };
  application:
    | { answers: ApplicationAnswers; submitted_at: string; updated_at: string;
        withdrawn: boolean;
        /** When a teacher last changed these answers — the day only, never who. */
        teacher_edited_at: string | null;
        /** When a teacher loaded these answers from a file — the day only, never who. */
        loaded_at: string | null }
    | null;
  status: ApplicationStatus;
  /** 🔴 THE PROJECT ROUND, ON THE SAME PAGE AND NOT BEHIND A CHOOSER. A student does not know
   *  this console has rounds; they know their teacher sent them a link. `null` means this
   *  cohort has no project form and never had an answer from this person. */
  project: ApplyProjectView | null;
  /** 🔴 A FACT ABOUT THE COHORT, NOT ABOUT THIS PERSON. A demo cohort's students were invented
   *  here, which is why its page may offer to put a tester on the roster. Whether THEY may
   *  join is the server's answer, in a sentence. */
  demo: boolean;
}

/** The intake form as an applicant is shown it. A teacher's editor gets the same one (on
 *  `TeamsOverview.student_form`), so the two draw exactly the same choices. */
export type IntakeFormView = NonNullable<ApplyCohortView["form"]>;

/** One student's answers, keyed by question id. The page joins it to `form.questions`;
 *  nothing here knows what `q3` means, which is what lets the question be rewritten.
 *  ⚠️ A TEAMMATES ANSWER IS A LIST of roster ids from `classmates`; every other is text. */
export type ApplyProjectBody = Record<string, string | string[]>;

/** Somebody a student may pick as a teammate: an opaque roster id and the name Classroom
 *  already shows the class. 🔴 NEVER AN ADDRESS OR A GOOGLE ACCOUNT ID — the server sends
 *  this list only to a student the roster matched. */
export interface Classmate {
  id: string;
  name: string;
}

export interface ApplyProjectView {
  course: { id: string; name: string };
  form: {
    state: FormState;
    intro: string;
    /** 🔴 THE QUESTIONS THEMSELVES. The page knows how to draw a `choice`, a `text`, a
     *  `link` and a `timezone`; the server says which is which and in what order. */
    questions: ProjectQuestion[];
    other_max: number;
    closes_at: string;
  };
  /** 🔴 WHY THEY MAY NOT ANSWER, IN A SENTENCE. There are four causes and three of them are
   *  nothing to do with the student — a missing scope, a roster nobody has synced, a roster
   *  Classroom returned no names for. Only the fourth is about them. */
  /** 🔴 `cause` IS WHAT DECIDES WHETHER THERE IS A BUTTON. `not_on_roster` is the one the
   *  reader can fix — they signed in with an account this cohort does not list — and the
   *  other three are a missing scope, a roster nobody has synced, and a roster Classroom
   *  returned no names for. Offering "sign in with another account" for those would send
   *  somebody round a loop that cannot work. */
  eligibility: {
    ok: boolean; reason: string;
    cause: "" | "no_scope" | "never_synced" | "no_names" | "not_on_roster";
  };
  intent:
    | { answers: ApplyProjectBody; submitted_at: string; updated_at: string;
        withdrawn: boolean;
        /** When a teacher last changed these answers — the day only, never who. */
        teacher_edited_at: string | null }
    | null;
  status: ApplicationStatus;
  team: { number: number; name: string; teammates: ApplyTeammate[] } | null;
  /** Who a Teammates question may pick. Empty unless the roster matched this student and the
   *  form asks for picks. */
  classmates: Classmate[];
}

/** The one build-log fact that is the student's own. 🔴 THE HANDLE AND NOTHING ELSE — no
 *  counts, no posts, on topic or otherwise. What was collected about somebody's timeline is
 *  teacher information, and the schema is built so an off-topic post is a number rather than
 *  a description; the server sends none of it here, which is why this page cannot show it. */
export interface Social { kind: string; handle: string }

/** One piece of work, from the console's own store and Classroom's listing merged.
 *  🔴 `points` IS NULL UNLESS A MARK ACTUALLY REACHED THEM. The console's own where
 *  `classroom_writes` records it went out, Classroom's own where the console has none, and
 *  nothing at all otherwise — `status` is what a reader gets then. A grade the agent produced
 *  and nobody sent is not a mark, and a HELD row is our failure rather than their zero. */
export interface ApplyWork {
  course_work_id: string;
  title: string;
  due: string;
  handed_in_at: string | null;
  status: "marked" | "being_marked" | "handed_in" | "not_handed_in";
  points: number | null;
  max_points: number | null;
  /** The note written FOR the student, already sanitised server-side. Never the teacher's. */
  note: string;
  /** 🔴 `label`, `earned` AND `weight` ONLY. The agent's reasoning and its quotes from the
   *  student's own repository are a judgement written after reading their code; the server
   *  strips them and this type is the shape of what is left. */
  criteria: { label: string; earned: number | null; weight: number | null }[];
}

/** Something that HAPPENED, with a date — never a list of states. A panel of states is a
 *  dashboard nobody reads twice; "your team was published" is news. */
export type Announcement =
  | { kind: "form_open"; course_id: string; course_name: string;
      round: "application" | "project"; closes_at: string; answered: boolean }
  | { kind: "team_published"; course_id: string; course_name: string; team: string;
      number: number | null; mates: string[] }
  | { kind: "marked"; course_id: string; course_name: string; course_work_id: string;
      title: string; points: number | null; max_points: number | null };

export interface ApplyProfileCohort {
  id: string;
  name: string;
  /** `teams.team_view`, unchanged and shared with the cohort page. ⚠️ THREE STATES AND NONE OF
   *  THEM IS AN ERROR: `pending` means nothing is published, `not_assigned` means a plan
   *  exists and this person is not in it — which a student needs the words for. */
  team: {
    status: "pending" | "not_assigned" | "team_assigned";
    team: { number: number; name: string; teammates: ApplyTeammate[] } | null;
  };
  work: ApplyWork[];
  /** ⚠️ ON THE COHORT, NOT A LIST BESIDE IT. `ApplyMe.applications` is the chooser's — every
   *  cohort somebody applied to, enrolled or not — and the Forms tab is about the one cohort
   *  it sits under. Null means no application row at all. */
  applied: { withdrawn: boolean } | null;
}

/** Exactly what the profile renders, and what BOTH doors produce: the student's own
 *  `/api/apply/me` and the teacher's `…/profile-preview` of the same person. */
export type ProfileData = Pick<ApplyMe, "account" | "announcements" | "cohorts">
  /** ⚠️ OPTIONAL: the teacher's preview does not carry it — nothing on that page may be a
   *  statement about whether the STUDENT teaches. */
  & Partial<Pick<ApplyMe, "is_teacher">>;

export interface ApplyMe {
  display_name: string;
  email: string;
  /** Whether this account is also an active teacher here. 🔴 WHAT STOPS THE CONSOLE'S FRONT
   *  DOOR SENDING A TEACHER TO A STUDENT PAGE they cannot leave: the root sends a browser with
   *  a student session and no teacher one to `/apply`, unless this is true. */
  is_teacher: boolean;
  open_cohorts: { id: string; name: string; closes_at: string }[];
  applications: { course_id: string; course_name: string; withdrawn: boolean }[];
  /** 🔴 THE PROFILE RIDES ON THE ROUTE THAT ALREADY ANSWERS "WHO IS SIGNED IN". A second
   *  endpoint would be a second place to get the principal wrong. */
  account: { display_name: string; email: string; picture: string;
             /** Their own intake answer, and the one contact detail a teammate is shown. */
             discord: string; social: Social };
  announcements: Announcement[];
  /** From the ROSTER, not from the open forms: a cohort whose forms have closed still has
   *  their team and their work on it. */
  cohorts: ApplyProfileCohort[];
}

export const applyMe = () => api<ApplyMe>("/apply/me");
/** 🔴 THE STUDENT'S OWN PAGE, BUILT BY THE SAME CODE, for a teacher of that cohort. It is
 *  scoped to this cohort deliberately: a teacher already sees this person's team, marks and
 *  handle here, and that somebody is also on another cohort is not a fact they hold. */
export const studentProfilePreview = (courseId: string, studentId: string) =>
  api<{ student_id: string; profile: ProfileData }>(
    `/courses/${encodeURIComponent(courseId)}/students/${encodeURIComponent(studentId)}`
    + "/profile-preview");
/** 🔴 NO STUDENT ID. Whose handle this is comes from the session, and the server writes it to
 *  every roster row that account matches — a link hangs off a roster row, so somebody on two
 *  cohorts has two of them and one box must not leave the other sweeping the old handle. */
export const setMyHandle = (kind: string, handle: string) =>
  api<{ ok: boolean; social: Social }>("/apply/handle", {
    method: "PUT",
    body: JSON.stringify({ kind, handle }),
  });
/** 🔴 THE DISCORD NAME A STUDENT SETS ON THEIR OWN CARD (2026-10-01). It used to live on the
 *  intake application alone, which a running cohort does not have. The server cleans it and
 *  answers with what its readers now show — which may be their application's name again, when
 *  this one is cleared. */
export const setMyDiscord = (discordUsername: string) =>
  api<{ ok: boolean; discord: string }>("/apply/discord", {
    method: "PUT",
    body: JSON.stringify({ discord_username: discordUsername }),
  });
export const applyCohort = (courseId: string) =>
  api<ApplyCohortView>(`/apply/${encodeURIComponent(courseId)}`);
export const putApplication = (courseId: string, body: ApplicationAnswers) =>
  api<ApplyCohortView>(`/apply/${encodeURIComponent(courseId)}/application`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
export const withdrawApplication = (courseId: string) =>
  api<ApplyCohortView>(`/apply/${encodeURIComponent(courseId)}/withdraw`, { method: "POST" });
export const putProjectIntent = (courseId: string, body: ApplyProjectBody) =>
  api<ApplyCohortView>(`/apply/${encodeURIComponent(courseId)}/project`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
/** 🔴 DEMO COHORTS ONLY. The server refuses any cohort Classroom knows about — a roster there
 *  is Classroom's — and refuses anybody this cohort's teachers and testers do not include. */
export const joinDemoCohort = (courseId: string) =>
  api<ApplyCohortView>(`/apply/${encodeURIComponent(courseId)}/join`, { method: "POST" });
export const withdrawProjectIntent = (courseId: string) =>
  api<ApplyCohortView>(`/apply/${encodeURIComponent(courseId)}/project/withdraw`,
                       { method: "POST" });
/** 🔴 A NAME AND NOTHING ELSE: the server renames the team this student is on, so there is no
 *  team id here to send. `""` clears it back to "Team N". */
export const renameMyTeam = (courseId: string, name: string) =>
  api<ApplyCohortView>(`/apply/${encodeURIComponent(courseId)}/project/team-name`, {
    method: "PUT",
    body: JSON.stringify({ name }),
  });
export const applyLogout = () =>
  api<{ signed_out: boolean }>("/apply/auth/logout", { method: "POST" });
/** Where a student starts a sign-in. The cohort id decides only which page they land on. */
export const APPLY_SIGN_IN_URL = (courseId: string) =>
  `/api/apply/auth/start?course=${encodeURIComponent(courseId)}`;

// ── the teacher's side ──────────────────────────────────────────────────────────

/** What a teacher configures. The questions travel beside it (`TeamsOverview.questions`) and
 *  are saved with it; `team_size_*` and `guidance` are kept on the wire and edited nowhere,
 *  because this round has grouped nobody since 2026-09-07. */
export interface ApplicationFormConfig {
  intro: string;
  team_size_min: number;
  team_size_max: number;
  enabled: boolean;
  closes_at: string;
  max_applications: number;
  guidance: string;
}

export interface ProposalSummary {
  id: string;
  status: "queued" | "running" | "ready" | "failed" | "published" | "superseded";
  requested_by: string;
  requested_at: string;
  finished_at: string;
  published_at: string;
  eligible_count: number;
  cost_usd: number | null;
  error: string;
}

export interface TeamsOverview {
  course: { id: string; name: string };
  form: ApplicationFormConfig;
  state: FormState;
  /** What an applicant will actually be asked — this cohort's own questions. */
  questions: IntakeQuestion[];
  /** The applicant's own form view, for a teacher's editor. */
  student_form: IntakeFormView;
  /** ⚠️ NO `eligible` AND NO `staff`. This round groups nobody, so there is nothing for an
   *  application to be eligible for; both moved to `ProjectsOverview.summary`. */
  /** ⚠️ THE STRIP'S NUMBERS ARE THE FILTERS' NUMBERS: `waiting`…`withdrawn` are counted by
   *  the server's one definition (`applications.standing`), the same one that puts each row
   *  under its filters (`TeacherApplicationRow.in`). */
  counts: {
    applications: number;
    withdrawn: number;
    excluded: number;
    live: number;
  } & Record<ApplicationFilter, number>;
  note: string;
}

/** What a running cohort asks before grouping. No option lists: the four domains are the
 *  programme's, and expertise is asked for as free text. */
export interface ProjectFormConfig {
  intro: string;
  /** What this cohort asks. Editable: add, remove, reword, reorder. */
  questions: ProjectQuestion[];
  team_size_min: number;
  team_size_max: number;
  enabled: boolean;
  closes_at: string;
  guidance: string;
  /** Which model reads this cohort's answers. "" is the console's default — never the
   *  default written down, or changing it later would be a no-op nobody could see. */
  model: string;
}

/** One model this console offers for a grouping run.
 *
 *  🔴 THE LIST COMES FROM THE SERVER, NOT FROM A COPY HERE. The gateway advertises models it
 *  cannot serve, so `GROUPING_MODELS` is hand-maintained against what actually answers — and
 *  a second copy in this file would be the one nobody re-probes. */
export interface GroupingModel {
  id: string;
  label: string;
  note: string;
  /** Non-empty means it cannot be picked, and this is the sentence saying why. */
  unavailable: string;
}

/** 🔴 THE ROSTER READING IS NOT DECORATION. A form open over an unsynced roster refuses every
 *  student who opens it, and the counts alone render that as "nobody has replied yet" — which
 *  is the same shape as a cohort that is simply slow. */
export interface RosterState {
  names: boolean;
  count: number;
  last_synced: string | null;
  note: string;
}

export interface ProjectSummary {
  received: number;
  live: number;
  withdrawn: number;
  excluded: number;
  /** 🔴 COUNTED APART FROM THE STUDENTS, so neither figure lies. A test submission among
   *  `received` would put "5 of 30 answered" on a page where four people replied, and would
   *  drop `outstanding` by one for somebody who was never going to reply. */
  tests: number;
  enrolled: number;
  outstanding: number;
  roster: RosterState;
}

/**
 * One question on the PROJECT form, which a teacher writes.
 *
 * 🔴 `id` IS SERVER-MINTED AND ANSWERS LIVE UNDER IT. Rewording a question keeps every answer
 * already given to it; a new question can never take a retired one's id, because the server
 * mints from the highest ever used rather than from the count.
 *
 * ⚠️ `grouping` IS WHAT THE MODEL READS and `share` is what a TEAMMATE reads. Both are per
 * question because the questions are per cohort: while the form was fixed these were two
 * hardcoded fields, and a form somebody writes could ask anything. `share` defaults off — an
 * answer reaches a classmate only deliberately.
 */
export interface ProjectQuestion {
  id: string;
  kind: "choice" | "text" | "link" | "timezone" | "teammates";
  label: string;
  hint: string;
  required: boolean;
  grouping: boolean;
  share?: boolean;
  /** Which grouping rule this answer feeds, if any.
   *
   *  🔴 ONLY ON A `choice` QUESTION, and the server refuses it anywhere else. The answers to a
   *  list of choices are strings the TEACHER wrote, so two people picking "Payments" pick the
   *  same string; on a text question the bucket would be whatever somebody typed. */
  axis?: "" | "domain" | "seniority";
  options?: string[];
  max?: number;
  /** A Teammates question's limit, 1 to 5. */
  max_picks?: number;
}

/** The prompt the grouping job sends, in three parts so the middle one can be marked.
 *
 *  🔴 THE JOB OWNS THIS AND THE SERVER KEEPS A COPY FOR DISPLAY.
 *  `test_the_prompt_preview_is_the_prompt` execs the real `scripts-teams/propose.py` and
 *  refuses anything but a byte-for-byte match — a preview that has drifted from the prompt is
 *  worse than no preview, because it is a teacher tuning one document while another is sent.
 *
 *  ⚠️ `before + guidance + after === full`, asserted server-side. Do not rebuild it here from
 *  parts in a different order; that is the one property this split could quietly lose. */
export interface PromptPreview {
  before: string;
  guidance: string;
  after: string;
  editable: boolean;
  full: string;
}

export interface ProjectsOverview {
  /** Which models a run may use, and which cannot be picked today and why.
   *
   *  ⚠️ ON THE OVERVIEW, NOT ON THE FORM. The form carries the CHOICE; this is what may be
   *  chosen, which is a fact about the console and the gateway rather than about this cohort.
   *  [X] Declaring it on `ProjectFormConfig` made the page read a key the route sends one
   *  level up — the shape of the defect that blanked this page once already. */
  models: GroupingModel[];
  course: { id: string; name: string };
  form: ProjectFormConfig;
  state: FormState;
  summary: ProjectSummary;
  /** 🔴 THE STUDENT'S OWN FORM PAYLOAD, so the console can render the actual questions with
   *  the same component a student fills in. Not a description of the form — a description
   *  can drift, and would be believed exactly when it was wrong. */
  student_form: ApplyProjectView["form"];
  /** 🔴 QUESTIONS THAT READ LIKE THEY ASK FOR IDENTITY. Advisory: the grouping job used to
   *  refuse any key outside an allowlist, and a teacher who writes the questions writes the
   *  keys — so this warns where it can no longer refuse. */
  /** The server's own identity wordlist. Sent rather than copied, so the editor can warn
   *  about a question that has not been saved yet — `identity_warnings` is derived from the
   *  STORED set and keyed by id, so it could never fire on a new one. */
  prompt: PromptPreview;
  identity_words: string[];
  identity_warnings: { id: string; label: string; word: string }[];
  proposals: ProposalSummary[];
  published_proposal_id: string;
  note: string;
}

/** One student's answers, for the teacher reviewing them. Carries their own words and none of
 *  the model's — `level`, `skills` and `rationale` belong to a PROPOSAL. */
export interface TeacherIntentRow {
  /** The roster row this answer belongs to. Present on every row, including one for
   *  somebody who has not answered — which is what makes "yet to answer" a filter with names
   *  behind it rather than a count. */
  student_id: string;
  id: string;
  display_name: string;
  email: string;
  /** Every answer, keyed by question id. Read with the form's questions. A pick is NOT here —
   *  it is roster ids, and `picks` carries it by name. */
  answers: Record<string, string>;
  /** Who this person picked, by name, and whether that classmate picked them back. */
  picks: { name: string; mutual: boolean }[];
  submitted_at: string;
  updated_at: string;
  /** When a teacher last changed these answers — never who; that is on the Logs page. */
  teacher_edited_at: string | null;
  version: number;
  withdrawn: boolean;
  excluded: boolean;
  /** 🔴 A SUBMISSION SOMEBODY MADE TO CHECK THE FORM, not an answer from a student. It is
   *  filtered out of every grouping run by a stored column and out of every count on the
   *  page; it is shown, marked, because a test nobody can see is not a test that arrived. */
  is_test: boolean;
  excluded_reason: string;
}

/** Where an application stands, as the filters on the Applications tab name it. */
export type ApplicationFilter =
  "waiting" | "accepted" | "enrolled" | "declined" | "aside" | "withdrawn";

export interface TeacherApplicationRow {
  id: string;
  applicant: { id: string; email: string; display_name: string;
               /** False for a person a file created by email, until their first sign-in. */
               signed_in: boolean };
  answers: ApplicationAnswers;
  full_name: string;
  discord_username: string;
  submitted_at: string;
  updated_at: string;
  /** When a teacher last changed these answers — never who; that is on the Logs page. */
  teacher_edited_at: string | null;
  /** When a teacher loaded this application from a file. */
  loaded_at: string | null;
  version: number;
  withdrawn_at: string;
  excluded_at: string;
  excluded_by: string;
  excluded_reason: string;
  is_staff: boolean;
  /** 🔴 A TEACHER'S DECISION, AND THE APPLICANT IS NOT TOLD (owner: *"later on publish"*). */
  decision: "" | "accepted" | "declined";
  decided_by: string;
  decided_at: string | null;
  decision_note: string;
  /** Found by the identity rule — the email on the cohort's roster — never set by hand. */
  on_roster: boolean;
  /** The one badge this row wears, and every filter it is listed under. */
  state: ApplicationFilter;
  in: ApplicationFilter[];
  /** A count; the thread is fetched for one application at a time. */
  comment_count: number;
}

/** One comment on an application, for the other teachers. */
export interface ApplicationComment {
  id: string;
  author: string;
  mine: boolean;
  /** "" once removed: the thread then says who removed it, in its place. */
  body: string;
  created_at: string;
  removed: boolean;
}

/** What loading a file would do, row by row — or did, once confirmed. */
export interface ApplicationImport {
  preview: boolean;
  written?: number;
  format: "csv" | "json";
  rows: number;
  counts: { new: number; attach: number; skipped: number; not_loaded: number };
  would_load: number;
  steps: {
    line: number; email: string; name: string;
    outcome: "new" | "attach" | "skipped" | "not_loaded";
    why: string; signed_in?: boolean; applied_at?: string;
    /** Grid answers left out because a row of the grid had no level. */
    dropped: string[];
  }[];
  matched: string[];
  unmatched: string[];
  unanswered: string[];
}

export interface Staleness {
  stale: boolean;
  added: number;
  removed: number;
  edits_only: boolean;
  /** Members the run never read, because a teacher placed them. Reported, never a reason to
   *  refuse a publish: consenting to your own change is not consent. */
  placed: number;
  fingerprint_now: string;
  fingerprint_then: string;
}

/** How one grouping rule came out for one team.
 *
 *  🔴 THREE WORDS AND A REASON, NEVER A NUMBER. `test_teams_privacy.py` lists `fit`, `score`
 *  and `rank` among the column names that must not exist — "no rank, no fit score" — and a
 *  score on this screen is one step from ranking the teams, which is one step from ranking the
 *  people in them. `state` is `met`, `not met` or `not measured`; `reason` carries the
 *  arithmetic for the two that are not `met` and is empty for the one that is.
 *
 *  ⚠️ RECOMPUTED ON READ. The arrangement is a pure function, so nothing stores this — which
 *  is also what stops a member move leaving the screen describing a plan that no longer
 *  exists. */
export interface TeamRule {
  rule: "size" | "domain" | "picks" | "seniority" | "complementarity" | "overlap";
  state: "met" | "not met" | "not measured";
  reason: string;
  /** Whose fact this is. `"form"` means the form does not configure the rule at all — the
   *  same sentence on every team, already stated on the Grouping rules tab — so a card drops
   *  it. `"team"` is about the people on this one and is always shown. */
  scope: "team" | "form";
}

export interface ProposalTeamMember {
  intent_id: string;
  /** The roster id, which is what the placement calls are keyed by. */
  student_id: string;
  display_name: string;
  email: string;
  /** Every answer, keyed by question id — read with `ProposalDetail.questions`. */
  answers: Record<string, string>;
  /** Who this person picked, and where that classmate ended up: `team` is the team number, or
   *  0 when they are not in this grouping at all. */
  picks: { name: string; mutual: boolean; team: number }[];
  /** The model's read, for the teacher who decides. Never on a student's own view. */
  level: string;
  skills: string[];
  rationale: string;
  /** Nobody read them: a teacher placed them after the run, which is why the team's rules
   *  say `not measured` where this person is the reason. */
  placed_by_hand: boolean;
}

/** Somebody on the cohort with no team in this plan. Keyed by STUDENT, because the one who
 *  never answered has no answer row until a placement makes one. */
export interface Unplaced {
  student_id: string;
  display_name: string;
  /** "" for somebody who has not answered. */
  intent_id: string;
  answered: boolean;
  why: string;
  answers: Record<string, string>;
}

/** One proposed team. ⚠️ NAMED RATHER THAN INLINE so the card's own helpers can be typed
 *  against it — an inline shape means every helper re-declares the fields it reads, and the
 *  copy that is not updated is the one that stops matching. */
export interface ProposalTeam {
  id: string;
  number: number;
  name: string;
  /** The model's read of the TEAM, for the teacher reviewing it. Empty until a run fills it. */
  rationale: string;
  members: ProposalTeamMember[];
  /** Which of the grouping rules this team meets, and why not where it does not. */
  rules: TeamRule[];
}

export interface ProposalDetail {
  /** ⚠️ THE QUESTIONS TRAVEL WITH THE PROPOSAL, because a plan is reviewed against the
   *  answers it was built from — and a form edited afterwards would otherwise leave this
   *  screen unable to say what `q3` was. */
  questions: ProjectQuestion[];
  id: string;
  status: ProposalSummary["status"];
  requested_by: string;
  requested_at: string;
  finished_at: string;
  published_at: string;
  published_by: string;
  model: string;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_usd: number | null;
  cost_source: string | null;
  eligible_count: number;
  error: string;
  notes: string[];
  prompt_sha256: string;
  teams: ProposalTeam[];
  /** The bounds this run was ASKED for, off the proposal rather than the live form — a size
   *  changed while a job was in flight must not re-describe a plan already built. */
  team_size_min: number;
  team_size_max: number;
  staleness: Staleness;
  /** Everybody with no team, on the same payload as the teams — the review used to be built
   *  from the memberships alone, so these people were a number and nothing to act on. */
  unplaced: Unplaced[];
}

/** One short name for the encoder, because these paths are mostly encoding. */
const enc = (id: string) => encodeURIComponent(id);

// ── the intake round: `/applications`. It groups nobody. ──────────────────────────
export const getTeams = (courseId: string) =>
  api<TeamsOverview>(`/courses/${enc(courseId)}/applications`);
/** ⚠️ `questions` LEFT OUT LEAVES THEM AS THEY ARE — the open/close switch sends none. */
export const putTeamForm = (courseId: string,
                            form: ApplicationFormConfig & { questions?: IntakeQuestion[] }) =>
  api<{ form: ApplicationFormConfig; state: FormState; changed: string[];
        questions: IntakeQuestion[] }>(
    `/courses/${enc(courseId)}/applications/form`,
    { method: "PUT", body: JSON.stringify(form) },
  );
export const getTeamApplications = (courseId: string) =>
  api<{ questions: IntakeQuestion[]; filters: ApplicationFilter[];
        rows: TeacherApplicationRow[] }>(`/courses/${enc(courseId)}/applications/list`);
/** A teacher changing one applicant's answers. The body is the applicant's own — only the
 *  answer fields — because the server refuses anything else. */
export const editApplicationAnswers = (
  courseId: string, applicationId: string, body: ApplicationAnswers) =>
  api<{ application_id: string; changed: string[]; version: number }>(
    `/courses/${enc(courseId)}/applications/${enc(applicationId)}/answers`,
    { method: "PUT", body: JSON.stringify(body) },
  );

export const excludeApplication = (
  courseId: string, applicationId: string, excluded: boolean, reason = "") =>
  api<{ application_id: string; excluded: boolean }>(
    `/courses/${enc(courseId)}/applications/${enc(applicationId)}/exclude`,
    { method: "POST", body: JSON.stringify({ excluded, reason }) },
  );
/** Accept, decline, or `""` to take a decision back. Teachers only; the applicant is not told. */
export const decideApplication = (
  courseId: string, applicationId: string, decision: "" | "accepted" | "declined",
  note = "") =>
  api<{ application_id: string; decision: string; changed: boolean;
        decided_at: string | null }>(
    `/courses/${enc(courseId)}/applications/${enc(applicationId)}/decision`,
    { method: "POST", body: JSON.stringify({ decision, note }) },
  );
export const getApplicationComments = (courseId: string, applicationId: string) =>
  api<{ application_id: string; comments: ApplicationComment[] }>(
    `/courses/${enc(courseId)}/applications/${enc(applicationId)}/comments`);
export const postApplicationComment = (courseId: string, applicationId: string, body: string) =>
  api<{ application_id: string; comments: ApplicationComment[] }>(
    `/courses/${enc(courseId)}/applications/${enc(applicationId)}/comments`,
    { method: "POST", body: JSON.stringify({ body }) },
  );
export const removeApplicationComment = (
  courseId: string, applicationId: string, commentId: string) =>
  api<{ application_id: string; comments: ApplicationComment[] }>(
    `/courses/${enc(courseId)}/applications/${enc(applicationId)}/comments/${enc(commentId)}`,
    { method: "DELETE" },
  );
/** A CSV or JSON file of applications, each row matched to a person by its email. Without
 *  `confirm` nothing is written and the answer is what would happen to every row. */
export const importApplications = (courseId: string, text: string, confirm = false) =>
  api<ApplicationImport>(`/courses/${enc(courseId)}/applications/import`, {
    method: "POST", body: JSON.stringify({ text, confirm }),
  });

// ── the project round: `/projects`. The only thing that builds a team. ────────────
export const getProjects = (courseId: string) =>
  api<ProjectsOverview>(`/courses/${enc(courseId)}/projects`);
/**
 * ⚠️ `changed` IS NEW HERE AND THE APPLICATIONS ROUND HAS ALWAYS HAD IT. The save bar names
 * what was written instead of a post-hoc "Saved" line that names nothing. `questions` appears
 * in it as a key, never as the wording — the server compares the set and reports the fact.
 */
export const putProjectForm = (courseId: string, form: ProjectFormConfig) =>
  api<{ form: ProjectFormConfig; state: FormState; changed: string[] }>(
    `/courses/${enc(courseId)}/projects/form`,
    { method: "PUT", body: JSON.stringify(form) },
  );
/** The answers in a shape another console can load, carrying no name and no address.
 *
 *  ⚠️ THE UPLOAD IS `importAnswers` BELOW, AND THIS COMMENT USED TO DENY IT EXISTED. The
 *  argument against a route — one mistyped cohort id from fabricating answers attributed to
 *  real students — is answered by `projects.import_refusal`, which turns away any cohort
 *  holding a row this console did not import and refuses `NEVER_WRITABLE` whatever its counts
 *  say. That property does not care whether the caller is a shell or a browser. */
export const exportIntents = (courseId: string) =>
  api<Record<string, unknown>>(`/courses/${enc(courseId)}/projects/export`);

/** Load a sheet of answers into a cohort nobody real is on.
 *
 *  🔴 PREVIEW THEN CONFIRM. Without `confirm` this parses the file, reports which columns
 *  landed on which questions and what it would refuse, and writes nothing — so "my Domain
 *  column matched nothing" is answerable before 43 people are stored with no domain.
 *
 *  ⚠️ `keep_identity` IS OFF BY DEFAULT. What makes a grouping worth testing is the shape of
 *  what people wrote, and none of it needs a name. */
export const importAnswers = (
  courseId: string,
  body: { csv: string; confirm?: boolean; replace?: boolean; keep_identity?: boolean },
) => api<Record<string, unknown>>(`/courses/${enc(courseId)}/projects/import`,
                                  { method: "POST", body: JSON.stringify(body) });

export const getProjectIntents = (courseId: string) =>
  api<{ rows: TeacherIntentRow[]; summary: ProjectSummary }>(
    `/courses/${enc(courseId)}/projects/intents`);
/** One student's answers as a teacher's editor needs them (owner, 2026-09-22).
 *
 *  ⚠️ THE RAW ANSWERS, PICK IDS INCLUDED, where the Answers list carries picks by name — an
 *  editor has to know which classmate is chosen, so it gets the ids and `classmates` to name
 *  them. `form` is the student's own form view, so the editor draws exactly what they see.
 *  `locked` is the server's rule stated in advance; the save decides again. */
export interface EditableAnswers {
  intent_id: string;
  display_name: string;
  form: ApplyProjectView["form"];
  answers: ApplyProjectBody;
  classmates: Classmate[];
  locked: string[];
  teacher_edited_at: string | null;
}

export const getIntentAnswers = (courseId: string, intentId: string) =>
  api<EditableAnswers>(
    `/courses/${enc(courseId)}/projects/intents/${enc(intentId)}/answers`);

/** Every answer, the way the student's save sends them — the server merges and validates. */
export const editIntentAnswers = (
  courseId: string, intentId: string, answers: ApplyProjectBody) =>
  api<{ intent_id: string; changed: string[]; version: number }>(
    `/courses/${enc(courseId)}/projects/intents/${enc(intentId)}/answers`,
    { method: "PUT", body: JSON.stringify(answers) },
  );

export const excludeIntent = (
  courseId: string, intentId: string, excluded: boolean, reason = "") =>
  api<{ intent_id: string; excluded: boolean; eligible: number }>(
    `/courses/${enc(courseId)}/projects/intents/${enc(intentId)}/exclude`,
    { method: "POST", body: JSON.stringify({ excluded, reason }) },
  );
/** Preview without `confirm`; queue with it. A preview writes nothing and costs nothing. */
export const requestProposal = (
  courseId: string, opts: { confirm?: boolean; include_staff?: boolean } = {}) =>
  api<Record<string, unknown>>(`/courses/${enc(courseId)}/projects/proposals`, {
    method: "POST",
    body: JSON.stringify({ confirm: false, include_staff: false, ...opts }),
  });
export const getProposal = (courseId: string, proposalId: string) =>
  api<ProposalDetail>(`/courses/${enc(courseId)}/projects/proposals/${enc(proposalId)}`);
export const moveMember = (
  courseId: string, proposalId: string, intentId: string, toTeamId: string) =>
  api<{ moved: boolean; team_id: string }>(
    `/courses/${enc(courseId)}/projects/proposals/${enc(proposalId)}/members`,
    { method: "PUT", body: JSON.stringify({ intent_id: intentId, to_team_id: toTeamId }) },
  );
export const publishTeams = (
  courseId: string, proposalId: string,
  opts: { confirm?: boolean; accept_stale?: boolean } = {}) =>
  api<Record<string, unknown>>(
    `/courses/${enc(courseId)}/projects/proposals/${enc(proposalId)}/publish`,
    { method: "POST",
      body: JSON.stringify({ confirm: false, accept_stale: false, ...opts }) },
  );
export const unpublishTeams = (courseId: string, proposalId: string) =>
  api<{ unpublished: string }>(
    `/courses/${enc(courseId)}/projects/proposals/${enc(proposalId)}/unpublish`,
    { method: "POST" },
  );
/** Rename or clear (`""`) a team, before or after publishing. Needs `can_release`, because a
 *  published name is on a student's page. */
/** Make a team out of people who have none. `studentIds`, never intents: somebody who never
 *  answered has no answer row until this creates one for them. */
export const createTeam = (
  courseId: string, proposalId: string, studentIds: string[], name = "") =>
  api<{ team_id: string; number: number; placed: string[]; created: boolean; moved: number }>(
    `/courses/${enc(courseId)}/projects/proposals/${enc(proposalId)}/teams`,
    { method: "POST", body: JSON.stringify({ student_ids: studentIds, name }) },
  );

/** Take away a team with nobody in it. The server refuses one that still has members, so a
 *  removal can never be a way to lift somebody out of the plan without saying where they went. */
export const removeTeam = (courseId: string, proposalId: string, teamId: string) =>
  api<{ removed: boolean; number: number }>(
    `/courses/${enc(courseId)}/projects/proposals/${enc(proposalId)}/teams/${enc(teamId)}`,
    { method: "DELETE" },
  );

/** Put people who have no team into one that already exists. */
export const addToTeam = (
  courseId: string, proposalId: string, teamId: string, studentIds: string[]) =>
  api<{ team_id: string; number: number; placed: string[]; created: boolean }>(
    `/courses/${enc(courseId)}/projects/proposals/${enc(proposalId)}/teams/${enc(teamId)}/members`,
    { method: "POST", body: JSON.stringify({ student_ids: studentIds }) },
  );

export const renameTeam = (
  courseId: string, proposalId: string, teamId: string, name: string) =>
  api<ProposalDetail>(
    `/courses/${enc(courseId)}/projects/proposals/${enc(proposalId)}/teams/${enc(teamId)}/name`,
    { method: "PUT", body: JSON.stringify({ name }) },
  );


// ── which cohorts this console serves, and which may be written to ─────────────

/** 🔴 READ-ONLY IS THE DEFAULT. Turning writing on is what lets a released mark and an
 *  announcement reach students, so it is preview-then-confirm and it is recorded. */
export const setWriteAllowed = (id: string, allowed: boolean, confirm: boolean) =>
  api<{ preview?: boolean; allowed: boolean; would_be?: boolean; was?: boolean; note: string }>(
    `${c(id)}/write-allowed`,
    { method: "PUT", body: JSON.stringify({ allowed, confirm }) });

/** ⚠️ THE FALLBACK FOR WHEN THE CREDENTIAL CANNOT LIST COURSES. Accepts the decimal id, the
 *  base64 form from the address bar, or a whole Classroom URL — a person copies whichever is
 *  in front of them, and the id in that URL is base64. */
export const addCohort = (courseId: string, name: string) =>
  api<{ course_id: string; name: string; added: boolean; granted: boolean;
        write_allowed: boolean }>(
    "/courses/add", { method: "POST", body: JSON.stringify({ course_id: courseId, name }) });

/** ⚠️ NOT COURSE-SCOPED — it asks which courses exist at all. It needs a Classroom scope the
 *  credential may not carry, and that refusal arrives as a 403 naming the scope. */
export const syncCourses = () =>
  api<{ listed: number; added: number; renamed: number; granted: number; read_only: string[] }>(
    "/courses/sync", { method: "POST" });
